/**
 * @fileoverview Day One — Generated Precedent Documents
 *
 * Purpose: View, preview, download, and track send status of generated
 * day-one precedent documents for a job.
 * URL: /dashboard/jobs/:jobId/day-one
 */

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "~/convex/_generated/api";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useActiveOrg } from "@/utils/org-context";
import { Badge, Spinner, Button, Input } from "@/ui/primitives";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/ui/composed/overlays/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/ui/composed/overlays/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/ui/composed/menus/dropdown-menu";
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import {
  Eye,
  Pencil,
  Check,
  ChevronDown,
  Copy,
  Download,
  ExternalLink,
  FileDown,
  Loader2,
  Plus,
  FileText,
  RefreshCw,
  RotateCcw,
  Settings,
  Sparkles,
  Star,
  Trash2,
  X,
  Paperclip,
} from "lucide-react";
import { DayOneDataTable } from "@/components/jobs/phases/day-one/DayOneDataTable";
import type { DayOnePrecedentRow } from "@/components/jobs/phases/day-one/types";
import type { DayOneTableMeta } from "@/components/jobs/phases/day-one/columns.meta";
import {
  collectAttachmentOnlyIds,
  nestGeneratedAttachments,
} from "@/components/jobs/phases/day-one/nest-generated-attachments";
import { cn } from "@/utils/misc";
import { extractErrorMessage } from "@/utils/errors";
import { useAutosave, AutosaveStatus } from "@/utils/forms";
import type { Doc, Id } from "~/convex/_generated/dataModel";
import type { FunctionReturnType } from "convex/server";
import {
  expandCollectionMarkup,
  hasCollectionMarkup,
} from "~/convex/jobs/precedents/collectionsShared";
import {
  buildSignatureImageToken,
  parseSignatureImageToken,
} from "~/convex/lib/signatureToken";
import {
  TemplateEditor,
  type TemplateEditorHandle,
} from "@/ui/composed/editor/template-editor";
import {
  buildFieldsLabelMap,
  collectPlaceholderKeysFromStoredTemplate,
  resolveVariantBlocks,
  fillVariantSnippetPlaceholders,
  stripInlineHighlights,
  extractVariantBlockInstances,
  type VariantSnippetForFill,
} from "@/ui/composed/editor/html-utils";
import { normalizeManualListMarkers } from "@/ui/composed/editor/manual-list-markers";
import {
  applyFieldModifiers,
  applyStackLayout,
  formatDateLong,
  formatInlineNameList,
  MODIFIER_CATALOG,
  modifierDecoration,
  NAME_LIST_FIELDS,
  parseModifiers,
  reflowCompanyStatus,
  serializeModifiers,
} from "@/ui/composed/editor/field-modifiers";
import {
  buildLetterHtml,
  buildWordBlob,
  downloadBlob,
  downloadAsWord,
  downloadAsPdf,
  downloadBulkAsWord,
  downloadBulkAsPdf,
  buildBulkWordBlob,
  letterPartsToPdfBlob,
  preserveEmptyParagraphs,
  type LetterParts,
  variantPaddingSources,
  pagePaddingInputs,
  getVariantsForParts,
} from "@/utils/export/exportHtmlDocument";
import { zoneLocksForPage } from "@/utils/branding/pageFit";
import {
  buildAttachmentDownloadPackage,
  buildZipArchive,
  consolidateAttachmentsIntoPdf,
  mergePdfBlobs,
} from "@/lib/precedent-attachments";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { toast } from "sonner";
import {
  CANONICAL_FIELDS_MAP,
  DEFAULT_CATEGORIES,
  FIELD_CATEGORIES,
} from "~/convex/settings/precedents/canonicalFields";
import {
  classifyFieldSource,
  type FieldSource,
} from "@/utils/precedents/classifyFieldSource";
import { preferTemplateFieldLabel } from "@/utils/precedents/templateFieldLabels";
import {
  collapseEmptyFieldLines,
  EMPTY_FIELD_MARKER,
  isResolvedFieldEmpty,
} from "@/utils/precedents/collapseEmptyFieldLines";
import { GALLAGHER_INSURANCE_FIELDS } from "@/lib/precedent-templates";
import { buildGallagherExportHtml } from "@/utils/precedents/gallagherExportHtml";
import {
  describeBlockedDownloads,
  fillBlockReason,
  partitionByFillError,
} from "@/utils/precedents/fillErrorGuard";
import { resolveDirectPdfUrl } from "@/utils/precedents/directPdf";

/**
 * One document in a bulk export (MOR-2342 BKY-09): either a letter rendered
 * from HTML (letters, Gallagher field table) or a ready-made PDF form.
 */
type BulkItem =
  | { kind: "letter"; doc: DayOneDoc; parts: LetterParts }
  | { kind: "pdf"; doc: DayOneDoc; url: string };

/** The letter parts of a bulk selection (PDF forms have none). */
function letterPartsOf(items: ReadonlyArray<BulkItem>): LetterParts[] {
  return items.flatMap((item) => (item.kind === "letter" ? [item.parts] : []));
}

/** A ready-made PDF fetched as a blob for bundling. */
async function fetchPdfBlob(url: string, name: string): Promise<Blob> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(
      `Could not fetch the PDF for "${name}" (${response.status})`,
    );
  }
  return response.blob();
}
import { getPresetPractitionerNames } from "@/lib/practitioner-presets";
import {
  buildGallagherPortalPayload,
  GALLAGHER_SOURCE_FIELD_KEYS,
  resolveGallagherFieldValues,
  type GallagherFieldConfig,
} from "~/convex/jobs/precedents/gallagherFieldConfig";
import { mapJobToCanonicalFields } from "~/convex/jobs/precedents/fieldMapping";
import {
  CONTACT_SOURCE_FIELDS,
  CONTACT_SOURCE_KIND_LABELS,
  parseContactSourceFieldKey,
  parseContactCustomFieldKey,
} from "~/convex/jobs/precedents/contactFields";
import {
  FIRM_CATEGORY_META,
  type FirmContactCategory,
} from "@/lib/contact-roles";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/ui/composed/overlays";
import {
  resolveDocumentPadding,
  type PageRule,
  type PaddingSource,
  type VariantFragments,
  resolvePagePadding,
} from "@/utils/branding/resolve-letterhead";
import { resolvePageNumbering } from "@/utils/branding/page-numbering";
import { buildLetterheadFragments } from "@/utils/branding/letterheadFragments";
import {
  resolveLetterheadForDoc,
  type LetterheadOverride,
  type ResolvedVariant,
} from "@/utils/branding/resolveVariants";
import { PageMarginsPopover } from "@/components/branding/PageMarginsPopover";
import { PaginatedLetterPreview } from "@/components/templates/PaginatedLetterPreview";
import { PdfViewer } from "@/ui/composed/data-display";
import { EditAttachmentsDialog } from "@/components/jobs/phases/day-one/EditAttachmentsDialog";
import { SharedAttachmentsDialog } from "@/components/jobs/phases/day-one/SharedAttachmentsDialog";
import { ContactDetailPanel } from "@/components/jobs/phases/contacts/ContactDetailPanel";
import { FirmContactDetailPanel } from "@/components/firm-contacts/FirmContactDetailPanel";

export const Route = createFileRoute(
  "/_app/_auth/dashboard/_layout/jobs/$jobId/day-one",
)({
  component: DayOnePage,
});

// ---------------------------------------------------------------------------
// Query result shapes
//
// Row aliases derived from the Convex queries this page consumes, so they
// track the backend return types automatically instead of restating them.
// ---------------------------------------------------------------------------

type JobData = FunctionReturnType<typeof api.jobs.queries.getJob>;

/**
 * A `jobs.precedents.queries.listByJob` row: the raw `jobPrecedents` doc
 * plus the resolved-URL / contact overlay the handler adds.
 */
type DayOneDoc = FunctionReturnType<
  typeof api.jobs.precedents.queries.listByJob
>[number];

/**
 * A `settings.precedents.queries.listByCategory` row: the template doc plus
 * the resolved storage URLs the handler adds.
 */
type TemplateDoc = FunctionReturnType<
  typeof api.settings.precedents.queries.listByCategory
>[number];

/**
 * `settings.branding.queries.getBrandingProfile` result: the profile doc
 * plus resolved image URLs and image-substituted header/footer HTML.
 */
type BrandingProfile = NonNullable<
  FunctionReturnType<typeof api.settings.branding.queries.getBrandingProfile>
>;

// ---------------------------------------------------------------------------
// Download progress toast (MOR-817)
// ---------------------------------------------------------------------------

/**
 * Toast body for bulk downloads: a determinate progress bar instead of a
 * spinner, since the document count is known up front. Re-rendered into the
 * same sonner toast (via its id) as each document completes.
 */
function DownloadProgressToast({
  label,
  completed,
  total,
}: {
  label: string;
  completed: number;
  total: number;
}) {
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  return (
    // Fixed width: sonner sizes the toast to its content, so a variable-width
    // body makes the toast (and the bar) jump around as document names of
    // different lengths cycle through. Long names truncate instead.
    <div className="flex w-72 max-w-full flex-col gap-1.5">
      <div className="flex items-center justify-between gap-3">
        <span className="min-w-0 flex-1 truncate text-xs font-medium text-primary">
          {label}
        </span>
        <span className="shrink-0 text-xs tabular-nums text-primary/50">
          {completed}/{total}
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-primary/10">
        <div
          className="h-full rounded-full bg-primary transition-[width] duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

function DayOnePage() {
  const { jobId } = Route.useParams();
  const navigate = useNavigate();
  const { orgId, activeOrg } = useActiveOrg();

  const { data: precedents, isLoading } = useQuery(
    convexQuery(api.jobs.precedents.queries.listByJob, {
      jobId: jobId as Id<"jobs">,
    }),
  );

  const { data: job } = useQuery(
    convexQuery(api.jobs.queries.getJob, { jobId: jobId as Id<"jobs"> }),
  );

  const practitionerOptions = useMemo(() => {
    if (!job) return [];
    const names = new Set<string>();
    for (const name of job.appointees ?? []) if (name) names.add(name);
    for (const name of job.practitioners ?? []) if (name) names.add(name);
    return Array.from(names);
  }, [job]);

  const { data: brandingProfile } = useQuery({
    ...convexQuery(api.settings.branding.queries.getBrandingProfile, {
      orgId: orgId as Id<"organizations">,
    }),
    enabled: !!orgId,
  });

  const { data: letterheadVariantsData } = useQuery({
    ...convexQuery(api.settings.branding.variantQueries.listVariantsForOrg, {
      orgId: orgId as Id<"organizations">,
    }),
    enabled: !!orgId,
  });
  // The inferred rows match `ResolvedVariant` except that the handler
  // resolves the scale / headerHtml / footerHtml fields to `null` where the
  // shared type (via the raw doc) says `undefined` — the assertion bridges
  // that gap until `ResolvedVariant` in resolveVariants.ts overrides them.
  const letterheadVariantsRaw = letterheadVariantsData as
    ResolvedVariant[] | undefined;

  // The asset library is the backwards-compat fallback — when no variant
  // matches a job, we synthesize a default from the org's letterhead
  // assets (one per zone, preferring the asset flagged `isDefault`).
  // Load ALL org templates (no category filter) so we can resolve any
  // jobPrecedent by templateKey. Job precedents may originate from any
  // template category (e.g. user-created custom categories like "Banks"),
  // and the legacy "day_one" filter was hiding their templateHtml here,
  // surfacing as "Preview not available — template not yet uploaded".
  //
  // Declared here (above resolveVariantsForDoc) so the resolver's deps
  // array can reference it without hitting a temporal dead zone.
  const { data: templates } = useQuery({
    ...convexQuery(api.settings.precedents.queries.listByCategory, {
      orgId: orgId as Id<"organizations">,
    }),
    enabled: !!orgId,
  });

  const { data: letterheadAssetsRaw } = useQuery({
    ...convexQuery(api.settings.branding.assetQueries.listAssetsForOrg, {
      orgId: orgId as Id<"organizations">,
    }),
    enabled: !!orgId,
  });

  // Variant groups available to the per-document VariantPicker rendered by
  // DayOnePreviewDialog. Lifted to DayOnePage scope so the value is in
  // scope where the dialog is mounted; same query is also called inside
  // the dialog (for the editor's snippet picker) and rides the same cache.
  const { data: pageVariantGroupsData } = useQuery({
    ...convexQuery(api.settings.precedents.variantGroups.listVariantGroups, {
      orgId: orgId as Id<"organizations">,
    }),
    enabled: !!orgId,
  });
  const variantGroupsForEditor = useMemo(
    () =>
      (pageVariantGroupsData ?? []).map((g) => ({
        slug: g.slug,
        name: g.name,
        options: g.options,
      })),
    [pageVariantGroupsData],
  );

  // Variant snippet library — feeds the placeholder fill so unbaked variant
  // options render their library content in the table-row download menus and
  // bulk export. The dialog runs its own copy of this query for live editing.
  const { data: pageVariantSnippetsData } = useQuery({
    ...convexQuery(api.settings.precedents.variantSnippets.listSnippets, {
      orgId: orgId as Id<"organizations">,
    }),
    enabled: !!orgId,
  });
  const pageVariantSnippets = useMemo<VariantSnippetForFill[]>(
    () =>
      (pageVariantSnippetsData ?? []).map((s) => ({
        _id: String(s._id),
        groupSlug: s.groupSlug,
        optionValue: s.optionValue,
        contentHtml: s.contentHtml,
      })),
    [pageVariantSnippetsData],
  );

  // Memoised resolver: turn raw variants -> ordered VariantFragments[]
  // for a given document, taking its letterheadOverride into account and
  // ordering by tag-match against the current job's canonical fields.
  // Unique bundle names in display order (used by the resolver to do
  // backwards-compat single-letterhead fallback and by the picker).
  const bundleNames = useMemo<string[]>(() => {
    if (!letterheadAssetsRaw) return [];
    const seen = new Set<string>();
    const ordered: string[] = [];
    for (const a of letterheadAssetsRaw) {
      const name = a.bundleName ?? a.sourceDocumentName ?? null;
      if (!name || seen.has(name)) continue;
      seen.add(name);
      ordered.push(name);
    }
    return ordered.sort((a, b) => a.localeCompare(b));
  }, [letterheadAssetsRaw]);

  const resolveVariantsForDoc = useCallback(
    (doc: DayOneDoc): VariantFragments[] => {
      if (
        !letterheadVariantsRaw ||
        !letterheadAssetsRaw ||
        doc.isThirdPartyForm
      )
        return [];

      // Resolution precedence: per-document override → per-template
      // override → firm default → single-bundle fallback → none.
      // The legacy `{ mode, variantIds }` shape (pre-migration rows) has no
      // `kind` and never matched the resolver's checks — treat it as unset.
      const rawDocOverride = doc.letterheadOverride;
      const docOverride: LetterheadOverride | undefined =
        rawDocOverride && "kind" in rawDocOverride ? rawDocOverride : undefined;
      const dbTemplate = templates?.find(
        (t) => t.templateKey === doc.templateKey,
      );
      const templateOverride: LetterheadOverride | undefined =
        dbTemplate?.letterheadOverride;
      const effectiveOverride = docOverride ?? templateOverride;
      const firmDefaultBundleName =
        brandingProfile?.firmDefaultLetterheadBundleName ?? null;
      const firmDefaultVariantId =
        brandingProfile?.firmDefaultLetterheadVariantId ?? null;

      const resolved = resolveLetterheadForDoc({
        override: effectiveOverride,
        firmDefaultBundleName,
        firmDefaultVariantId,
        variants: letterheadVariantsRaw,
        bundles: bundleNames,
      });

      // Explode the winning letterhead into one render fragment per
      // (zone, image, rule). A zone can hold several images — e.g. a
      // first-page header plus an every-other-page header (MOR-953) — and
      // pickVariantForPage walks the fragments per page, so all of them
      // must be emitted. Shared with the Settings > Docs preview.
      return buildLetterheadFragments(resolved, letterheadAssetsRaw);
    },
    [
      letterheadVariantsRaw,
      letterheadAssetsRaw,
      brandingProfile,
      bundleNames,
      templates,
    ],
  );

  const { mutateAsync: setVariantSelectionsMut } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.precedents.mutations.setVariantSelections,
    ),
  });

  const { mutateAsync: setSnippetSelectionsMut } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.precedents.mutations.setSnippetSelections,
    ),
  });

  const { mutateAsync: setLetterheadOverride } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.precedents.mutations.setLetterheadOverride,
    ),
  });

  const { mutateAsync: setWorkflowStatus } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.precedents.mutations.setWorkflowStatus,
    ),
  });

  const { mutateAsync: deletePrecedent } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.precedents.mutations.deletePrecedent,
    ),
  });

  const { mutateAsync: bulkDeletePrecedents } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.precedents.mutations.bulkDeletePrecedents,
    ),
  });

  const { data: allPresets } = useQuery({
    ...convexQuery(api.settings.precedents.presets.listByOrg, {
      orgId: orgId as Id<"organizations">,
    }),
    enabled: !!orgId,
  });

  // Joint appointees must be registered practitioners the firm has configured
  // under Settings → Fields → Practitioner Details, not arbitrary job team
  // members, so the second practitioner picker draws on presets instead.
  const presetPractitionerOptions = useMemo(
    () => getPresetPractitionerNames(allPresets),
    [allPresets],
  );

  const { data: favouriteIds } = useQuery({
    ...convexQuery(api.settings.precedents.favourites.listByUser, {
      orgId: orgId as Id<"organizations">,
    }),
    enabled: !!orgId,
  });

  const { data: currentUser } = useQuery(
    convexQuery(api.users.queries.getCurrentUser, {}),
  );

  const visiblePrecedents = useMemo(() => {
    if (!precedents) return undefined;
    return precedents;
  }, [precedents]);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sharedAttachmentsOpen, setSharedAttachmentsOpen] = useState(false);
  // Bulk downloads render every letter through html2canvas, which can take
  // tens of seconds for a large selection — surface a spinner on the toolbar
  // button plus a loading toast so the request visibly stays in flight
  // rather than looking failed (MOR-817).
  const [isBulkDownloading, setIsBulkDownloading] = useState(false);
  // Preview dialog is lifted out of the row component so the table can
  // stay presentational. `previewDocId` is the row currently open; when
  // null, the dialog isn't mounted at all.
  const [previewDocId, setPreviewDocId] = useState<Id<"jobPrecedents"> | null>(
    null,
  );

  // Contact detail slide-outs, opened by clicking a contact in the table.
  // Job-contact rows open the full job contact panel; firm-contact rows
  // open the firm directory panel.
  const [viewingJobContactId, setViewingJobContactId] =
    useState<Id<"jobContacts"> | null>(null);
  const [viewingFirmContactId, setViewingFirmContactId] =
    useState<Id<"firmContacts"> | null>(null);
  // For the job contact panel (its merge picker needs the job's contact rows).
  const { data: jobContacts } = useQuery(
    convexQuery(api.contacts.queries.listJobContacts, {
      jobId: jobId as Id<"jobs">,
    }),
  );

  // "Generate draft email" (MOR-529): the mail agent drafts a cover email per
  // selected document; drafts land in the job Mail page's For Review tab.
  const { data: jobMailbox } = useQuery(
    convexQuery(api.mail.queries.getMailboxForJob, {
      jobId: jobId as Id<"jobs">,
    }),
  );
  // Rows with a Morgan draft awaiting review — the Actions cell swaps the
  // Generate button for a "Ready for review in mailbox" badge. Drafts only land in
  // this query after the mail agent creates them, so we also track
  // optimistic ids from the moment the user clicks.
  const { data: pendingDayOneDrafts } = useQuery(
    convexQuery(api.mail.dayOneSend.listPendingDayOneDrafts, {
      jobId: jobId as Id<"jobs">,
    }),
  );
  const [draftRequestedIds, setDraftRequestedIds] = useState(
    () => new Set<string>(),
  );
  // Once the server reports a pending draft, drop the optimistic entry so
  // approving/rejecting the draft can restore the Generate button.
  useEffect(() => {
    if (!pendingDayOneDrafts?.length) return;
    setDraftRequestedIds((prev) => {
      const next = new Set(prev);
      for (const d of pendingDayOneDrafts) {
        next.delete(String(d.jobPrecedentId));
      }
      return next.size !== prev.size ? next : prev;
    });
  }, [pendingDayOneDrafts]);
  const pendingDraftPrecedentIds = useMemo(() => {
    const ids = new Set(
      (pendingDayOneDrafts ?? []).map((d: { jobPrecedentId: string }) =>
        String(d.jobPrecedentId),
      ),
    );
    for (const id of draftRequestedIds) ids.add(id);
    return ids;
  }, [pendingDayOneDrafts, draftRequestedIds]);
  const { mutateAsync: requestAgentSend } = useMutation({
    mutationFn: useConvexMutation(api.mail.dayOneSend.requestAgentSend),
  });
  const { mutateAsync: generateMailUploadUrl } = useMutation({
    mutationFn: useConvexMutation(api.mail.dayOneSend.generateUploadUrl),
  });

  const handleBulkDelete = useCallback(async () => {
    if (selectedIds.size === 0) return;
    const count = selectedIds.size;
    try {
      await bulkDeletePrecedents({
        precedentIds: Array.from(selectedIds) as Id<"jobPrecedents">[],
      });
      setSelectedIds(new Set());
      toast.success(`Deleted ${count} document${count === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to delete documents"));
    }
  }, [selectedIds, bulkDeletePrecedents]);

  const handleDeletePrecedent = useCallback(
    async (precedentId: Id<"jobPrecedents">, name: string) => {
      try {
        await deletePrecedent({ precedentId });
        setSelectedIds((prev) => {
          if (!prev.has(precedentId)) return prev;
          const next = new Set(prev);
          next.delete(precedentId);
          return next;
        });
        toast.success(`Deleted "${name}"`);
      } catch (err) {
        toast.error(extractErrorMessage(err, "Failed to delete precedent"));
      }
    },
    [deletePrecedent],
  );

  // Row-level select toggles + select-all are now handled internally by
  // the `DayOneDataTable` via TanStack table's `rowSelection` state +
  // the column-defined header checkbox; the page only owns the
  // current selection set so the bulk-action toolbar can render.

  // Project the live precedents query into the nested pack/attachment
  // shape the data table consumes. Generated-precedent siblings fold
  // under the cover letter that attaches them so a director pack reads
  // as one sendable document. Memoised so TanStack doesn't rebuild its
  // row model on every render.
  const tableRows = useMemo<DayOnePrecedentRow[]>(() => {
    if (!visiblePrecedents) return [];
    return nestGeneratedAttachments(visiblePrecedents);
  }, [visiblePrecedents]);

  const attachmentOnlyIds = useMemo(
    () => collectAttachmentOnlyIds(tableRows),
    [tableRows],
  );

  const stats = useMemo(() => {
    return {
      total: tableRows.length,
      attachments: attachmentOnlyIds.size,
    };
  }, [tableRows, attachmentOnlyIds]);

  // The currently-previewing precedent's full row (with editedHtml,
  // attachments, etc.) — looked up from the live query so toggling
  // `previewDocId` in either direction renders against fresh data.
  const previewDoc = useMemo(() => {
    if (!previewDocId || !visiblePrecedents) return null;
    return visiblePrecedents.find((p) => p._id === previewDocId) ?? null;
  }, [previewDocId, visiblePrecedents]);

  /**
   * Resolve a doc's HTML-letter attachments (siblings without a
   * `filledPdfStorageId`) into renderable `LetterParts`.
   *
   * PDF-form attachments resolve via `generatedFilledPdfUrl` and are fetched
   * directly by `fetchAttachmentBlobs`. HTML-letter attachments don't have
   * a backing storage URL — they need to be rendered client-side from the
   * sibling precedent's body html using the same letterhead / branding /
   * signature pipeline as the main document. This callback closes over the
   * page-level data so the per-row download menu can build the renderable
   * parts on demand without prop-drilling everything into the menu.
   */
  const buildHtmlLetterAttachmentPartsForDoc = useCallback(
    (
      doc: DayOneDoc,
    ): Array<{ name: string; cacheKey: string; parts: LetterParts }> => {
      if (!visiblePrecedents) return [];
      const result: Array<{
        name: string;
        cacheKey: string;
        parts: LetterParts;
      }> = [];
      for (const attachment of doc.attachments ?? []) {
        if (attachment.status !== "resolved") continue;
        // Skip attachments handled by `fetchAttachmentBlobs`:
        // - `downloadUrl` (uploaded files)
        // - `generatedFilledPdfUrl` (PDF-form sibling precedents)
        if (attachment.downloadUrl || attachment.generatedFilledPdfUrl)
          continue;
        if (!attachment.generatedPrecedentId) continue;

        const sibling = visiblePrecedents.find(
          (p) => p._id === attachment.generatedPrecedentId,
        );
        // Defensive: a PDF-form sibling would already have been routed via
        // `generatedFilledPdfUrl` above; if `filledPdfStorageId` is set here
        // it means the URL hasn't materialised yet, so skip rather than
        // double-rendering as a letter.
        if (!sibling || sibling.filledPdfStorageId) continue;
        // MOR-2342 — a form whose fill failed has no current document; never
        // render it as a letter-style annexure in its place.
        if (fillBlockReason(sibling)) continue;

        const siblingTemplate = templates?.find(
          (t) => t.templateKey === sibling.templateKey,
        );
        const siblingParts = getDocumentParts(
          sibling,
          brandingProfile,
          siblingTemplate,
          currentUser ?? null,
          resolveVariantsForDoc(sibling),
          mergeVariantSelections(
            job?.variantSelections,
            sibling.variantSelections,
          ),
          pageVariantSnippets,
          sibling.snippetSelections,
        );
        if (!siblingParts.bodyHtml) continue;

        result.push({
          name:
            attachment.generatedPrecedentName ??
            sibling.name ??
            attachment.name,
          // Stable identity for the per-run render cache: the same annexure
          // attached to several packs renders once per bulk download.
          cacheKey: String(sibling._id),
          parts: siblingParts,
        });
      }
      return result;
    },
    [
      visiblePrecedents,
      templates,
      brandingProfile,
      currentUser,
      resolveVariantsForDoc,
      job?.variantSelections,
      pageVariantSnippets,
    ],
  );

  // Send-with-Morgan indirection: the real handler is defined after
  // `buildBulkItems` (it renders the letter PDFs through the same pipeline
  // as the Download button), but `tableMeta` is built before it — a ref
  // avoids the use-before-declaration while keeping meta deps stable.
  const sendWithMorganRef = useRef<((ids: string[]) => Promise<void>) | null>(
    null,
  );

  // Clicking a pack or generated attachment opens the preview dialog;
  // clicking a nested file attachment opens the stored file itself (it has
  // no precedent to preview) — MOR-971.
  const openDayOneRow = useCallback((row: DayOnePrecedentRow) => {
    if (row.fileAttachment) {
      if (row.fileAttachment.downloadUrl) {
        window.open(row.fileAttachment.downloadUrl, "_blank", "noopener");
      } else {
        toast.error("This attachment hasn't been uploaded yet.");
      }
      return;
    }
    setPreviewDocId(row._id);
  }, []);

  // Side-channel callbacks injected via TanStack table `meta` so column
  // cells can preview / generate Morgan drafts / delete without prop drilling.
  // `renderDownloadMenu` lets us keep the per-row download logic
  // (which depends on branding + templates + signature) inside this
  // page module rather than dragging it into the data table file.
  const tableMeta = useMemo<DayOneTableMeta>(
    () => ({
      onPreview: (row) => openDayOneRow(row),
      orgId,
      onWorkflowStatusChange: (id, statusId) => {
        void setWorkflowStatus({ precedentIds: [id], statusId }).catch((err) =>
          toast.error(
            extractErrorMessage(err, "Failed to update workflow status"),
          ),
        );
      },
      onDelete: (id, name) => handleDeletePrecedent(id, name),
      pendingDraftPrecedentIds,
      onOpenContact: (row) => {
        if (row.jobContactId) setViewingJobContactId(row.jobContactId);
        else if (row.firmContactId) setViewingFirmContactId(row.firmContactId);
      },
      onSendWithMorgan:
        jobMailbox?.status === "active"
          ? (id) => {
              void sendWithMorganRef.current?.([id]);
            }
          : null,
      renderDownloadMenu: (row) => {
        const liveDoc = visiblePrecedents?.find((p) => p._id === row._id);
        if (!liveDoc) return null;
        return (
          <DocumentDownloadMenu
            doc={liveDoc}
            brandingProfile={brandingProfile}
            dbTemplate={templates?.find(
              (t) => t.templateKey === liveDoc.templateKey,
            )}
            signatureData={currentUser ?? null}
            variants={resolveVariantsForDoc(liveDoc)}
            variantSelections={mergeVariantSelections(
              job?.variantSelections,
              liveDoc.variantSelections,
            )}
            variantSnippets={pageVariantSnippets}
            snippetSelections={liveDoc.snippetSelections}
            buildHtmlLetterAttachmentParts={() =>
              buildHtmlLetterAttachmentPartsForDoc(liveDoc)
            }
          />
        );
      },
    }),
    [
      handleDeletePrecedent,
      openDayOneRow,
      orgId,
      setWorkflowStatus,
      visiblePrecedents,
      brandingProfile,
      templates,
      currentUser,
      resolveVariantsForDoc,
      job?.variantSelections,
      pageVariantSnippets,
      buildHtmlLetterAttachmentPartsForDoc,
      pendingDraftPrecedentIds,
      jobMailbox?.status,
    ],
  );

  // Returns the requested letters (default: current selection) paired with
  // their source doc, so bulk download can both render the combined letter
  // AND bundle each doc's attachments (the doc reference is needed to fetch
  // attachment blobs). Send with Morgan reuses this to render the exact
  // same PDF a download would produce.
  const buildBulkItems = useCallback(
    (ids?: ReadonlyArray<string>): BulkItem[] => {
      if (!visiblePrecedents) return [];
      const wanted = ids ? new Set(ids) : selectedIds;
      // MOR-2342 — documents whose PDF fill failed are excluded here; the
      // callers tell the user which ones and why (see `reportBlockedFills`).
      const selected = partitionByFillError(
        visiblePrecedents.filter((p) => wanted.has(p._id)),
      ).ready;
      const items: BulkItem[] = [];
      for (const doc of selected) {
        const dbTemplate = templates?.find(
          (t) => t.templateKey === doc.templateKey,
        );
        const rawBodyHtml = dbTemplate?.templateHtml;
        const mergedSel = mergeVariantSelections(
          job?.variantSelections,
          doc.variantSelections,
        );
        const bodyHtml = rawBodyHtml
          ? resolveVariantsForRender(
              rawBodyHtml,
              mergedSel,
              pageVariantSnippets,
              doc.snippetSelections,
            )
          : rawBodyHtml;
        const effectiveHtml = rawBodyHtml
          ? pickHtmlForRender(doc.editedHtml, rawBodyHtml)
          : doc.editedHtml;
        const editedHtml =
          effectiveHtml && effectiveHtml === doc.editedHtml
            ? resolveVariantsForRender(
                effectiveHtml,
                mergedSel,
                pageVariantSnippets,
                doc.snippetSelections,
              )
            : undefined;
        // MOR-2342 — the Gallagher notification has no HTML body; render its
        // field table so it is no longer silently left out of bulk downloads.
        const isGallagher = doc.templateKey === "form_gallagher";
        if (!bodyHtml && !editedHtml && !isGallagher) {
          // MOR-2342 BKY-09 — a PDF form (filled, or a flat source PDF) has
          // no HTML to render; it ships as the ready-made PDF instead of
          // being silently left out of the bulk export.
          const url = resolveDirectPdfUrl(doc, dbTemplate);
          if (url) items.push({ kind: "pdf", doc, url });
          continue;
        }
        {
          const enrichedValues = enrichWithSignature(
            doc.fieldValues ?? {},
            currentUser,
          );
          // MOR-382: Inject `&nbsp;` into empty paragraphs so manual blank
          // lines from the editor survive into the rendered document. Browsers
          // collapse `<p></p>` margins so 5 empty paragraphs visually became
          // a single ~0.75em gap; Word's altChunk dropped them entirely.
          // editedHtml gets current field values substituted too — its field
          // chips persist as `{{key}}` tokens, so Edit Fields changes made
          // after a content edit reach bulk downloads (MOR-704).
          const bodyContent = isGallagher
            ? buildGallagherExportHtml(doc.fieldValues ?? {}, {
                title: doc.name,
                fieldConfig: dbTemplate?.fieldConfig,
              })
            : preserveEmptyParagraphs(
                replaceFieldPlaceholders(
                  editedHtml ?? bodyHtml ?? "",
                  enrichedValues,
                  {
                    checkboxKeys: collectCheckboxKeys(dbTemplate),
                    dateKeys: collectDateKeys(dbTemplate),
                    collectionData: doc.collectionData,
                  },
                ),
              );
          const docVariants = resolveVariantsForDoc(doc);
          items.push({
            kind: "letter",
            doc,
            parts: {
              bodyHtml: bodyContent,
              // Pass ordered variants — these supersede the legacy flat
              // headerImageUrl/etc. fields. Third-party forms get [] which
              // disables letterhead entirely.
              variants: docVariants,
              brandingPadding: !doc.isThirdPartyForm
                ? brandingProfile
                : undefined,
              templatePadding: !doc.isThirdPartyForm ? dbTemplate : undefined,
              documentPadding: !doc.isThirdPartyForm ? doc : undefined,
              templatePageOverrides: !doc.isThirdPartyForm
                ? (dbTemplate?.pageMarginOverrides ?? null)
                : null,
              documentPageOverrides: !doc.isThirdPartyForm
                ? (doc.pageMarginOverrides ?? null)
                : null,
              defaultFontFamily: !doc.isThirdPartyForm
                ? (brandingProfile?.editorFontFamily ?? undefined)
                : undefined,
              defaultLineHeight: !doc.isThirdPartyForm
                ? (brandingProfile?.editorLineHeight ?? undefined)
                : undefined,
              bodyTextColor: !doc.isThirdPartyForm
                ? (brandingProfile?.bodyTextColor ?? undefined)
                : undefined,
              defaultFontSize: !doc.isThirdPartyForm
                ? (brandingProfile?.editorFontSize ?? undefined)
                : undefined,
              defaultParagraphSpacingBefore: !doc.isThirdPartyForm
                ? (brandingProfile?.editorParagraphSpacingBefore ?? undefined)
                : undefined,
              defaultParagraphSpacingAfter: !doc.isThirdPartyForm
                ? (brandingProfile?.editorParagraphSpacingAfter ?? undefined)
                : undefined,
              pageNumbering: !doc.isThirdPartyForm
                ? resolvePageNumbering(brandingProfile, dbTemplate)
                : null,
            },
          });
        }
      }
      return items;
    },
    [
      visiblePrecedents,
      selectedIds,
      templates,
      brandingProfile,
      currentUser,
      resolveVariantsForDoc,
      job?.variantSelections,
      pageVariantSnippets,
    ],
  );

  /**
   * "Generate draft email" (MOR-529): renders each letter through the SAME
   * pipeline as the Download button (branding, letterhead, signature,
   * variants → PDF), uploads the PDFs, and schedules the mail-agent
   * drafting runs. Forms with a filled PDF skip the client render — the
   * backend attaches `filledPdfStorageId` directly.
   */
  /**
   * MOR-2342 — name any selected documents whose fill failed (they are left
   * out of bulk output rather than exported as a blank or stale PDF).
   */
  const reportBlockedFills = useCallback(
    (ids: ReadonlyArray<string>, items?: ReadonlyArray<BulkItem>) => {
      const wanted = new Set(ids);
      const selected = (visiblePrecedents ?? []).filter((p) =>
        wanted.has(p._id),
      );
      const { ready, blocked } = partitionByFillError(selected);
      // MOR-2342 BKY-09 — never omit silently: a selected document that is
      // neither a letter nor a ready-made PDF is named with the reason.
      const included = new Set((items ?? []).map((item) => item.doc._id));
      const unexportable = items
        ? ready
            .filter((doc) => !included.has(doc._id))
            .map((doc) => ({
              doc,
              reason: "it has no generated content or PDF to export yet",
            }))
        : [];
      const omitted = [...blocked, ...unexportable];
      if (omitted.length > 0) toast.error(describeBlockedDownloads(omitted));
    },
    [visiblePrecedents],
  );

  const sendWithMorgan = useCallback(
    async (ids: string[]) => {
      if (ids.length === 0) return;
      // Hide Generate immediately — draft may take a while to appear in Mail.
      setDraftRequestedIds((prev) => {
        const next = new Set(prev);
        for (const id of ids) next.add(id);
        return next;
      });
      const toastId = toast.loading("Preparing documents for Morgan...");
      try {
        // MOR-2342 — documents whose fill failed are not sent at all (the
        // server refuses them too); only the ready ids reach the request.
        reportBlockedFills(ids);
        const wanted = new Set(ids);
        const readyIds = partitionByFillError(
          (visiblePrecedents ?? []).filter((p) => wanted.has(p._id)),
        ).ready.map((p) => p._id as string);
        setDraftRequestedIds((prev) => {
          const next = new Set(prev);
          for (const id of ids) if (!readyIds.includes(id)) next.delete(id);
          return next;
        });
        if (readyIds.length === 0) {
          toast.dismiss(toastId);
          return;
        }
        const items = buildBulkItems(readyIds);
        const renderedPdfs: Array<{
          jobPrecedentId: Id<"jobPrecedents">;
          storageId: Id<"_storage">;
        }> = [];
        for (const item of items) {
          if (item.kind !== "letter") continue; // Form: server attaches its PDF.
          const { doc, parts } = item;
          const blob = await letterPartsToPdfBlob(parts, doc.name);
          const uploadUrl = await generateMailUploadUrl({
            jobId: jobId as Id<"jobs">,
          });
          const res = await fetch(uploadUrl, {
            method: "POST",
            headers: { "Content-Type": "application/pdf" },
            body: blob,
          });
          if (!res.ok) throw new Error(`PDF upload failed (${res.status})`);
          const { storageId } = (await res.json()) as {
            storageId: Id<"_storage">;
          };
          renderedPdfs.push({ jobPrecedentId: doc._id, storageId });
        }

        const result = await requestAgentSend({
          jobId: jobId as Id<"jobs">,
          jobPrecedentIds: readyIds as Id<"jobPrecedents">[],
          renderedPdfs: renderedPdfs.length > 0 ? renderedPdfs : undefined,
        });
        toast.dismiss(toastId);
        if (result.scheduled > 0) {
          toast.success(
            `Morgan is drafting ${result.scheduled} email${
              result.scheduled === 1 ? "" : "s"
            }. Review ${result.scheduled === 1 ? "it" : "them"} in Mail → For Review.`,
          );
        }
        for (const skip of result.skipped) {
          toast.warning(`Skipped one document: ${skip.reason}`);
          setDraftRequestedIds((prev) => {
            const next = new Set(prev);
            next.delete(String(skip.precedentId));
            return next;
          });
        }
      } catch (err) {
        toast.dismiss(toastId);
        setDraftRequestedIds((prev) => {
          const next = new Set(prev);
          for (const id of ids) next.delete(id);
          return next;
        });
        toast.error(extractErrorMessage(err, "Failed to send to Morgan"));
      }
    },
    [
      buildBulkItems,
      reportBlockedFills,
      visiblePrecedents,
      generateMailUploadUrl,
      requestAgentSend,
      jobId,
    ],
  );
  sendWithMorganRef.current = sendWithMorgan;

  /** Bulk-toolbar variant: sends the current selection, then clears it. */
  const handleSendWithMorgan = useCallback(async () => {
    if (selectedIds.size === 0) return;
    const packIds = Array.from(selectedIds).filter(
      (id) => !attachmentOnlyIds.has(id),
    );
    if (packIds.length === 0) {
      toast.error(
        "Select the original document to draft an email — attachments are sent with the pack.",
      );
      return;
    }
    await sendWithMorgan(packIds);
    setSelectedIds(new Set());
  }, [selectedIds, sendWithMorgan, attachmentOnlyIds]);

  /**
   * Gather a single document's attachments as downloadable blobs — the same
   * two channels the per-row download menu uses: PDF-form siblings + uploads
   * via `fetchAttachmentBlobs`, and HTML-letter siblings rendered client-side.
   * Returned blobs are tagged with the owning document name so the bulk
   * bundlers can namespace them and avoid filename collisions across docs.
   */
  const gatherDocAttachmentBlobs = useCallback(
    async (
      doc: DayOneDoc,
      caches?: BulkDownloadCaches,
    ): Promise<Array<{ fileName: string; file: Blob; fileType?: string }>> => {
      // Fetch uploaded/PDF-form attachments and render HTML-letter
      // annexures concurrently — the two channels are independent, and the
      // caches let annexures shared across packs be fetched/rendered once
      // per bulk run (MOR-971).
      const [pdfAttachments, renderedHtmlLetters] = await Promise.all([
        fetchAttachmentBlobs(doc, caches?.attachmentBlobs),
        Promise.all(
          buildHtmlLetterAttachmentPartsForDoc(doc).map(
            async ({ name, cacheKey, parts }) => {
              const filePromise =
                caches?.renderedLetterPdfs.get(cacheKey) ??
                (() => {
                  const created = letterPartsToPdfBlob(parts, name);
                  caches?.renderedLetterPdfs.set(cacheKey, created);
                  return created;
                })();
              return {
                fileName: `${sanitizeFilename(name)}.pdf`,
                file: await filePromise,
                fileType: "application/pdf" as const,
              };
            },
          ),
        ),
      ]);
      return [...pdfAttachments, ...renderedHtmlLetters];
    },
    [buildHtmlLetterAttachmentPartsForDoc],
  );

  // Bulk downloads operate on packs only. Annexure-only rows are excluded
  // from the selection UI, but filter defensively here too: an annexure is
  // already merged into / zipped under every pack that attaches it, so
  // downloading it standalone as well duplicated files (MOR-971).
  const bulkDownloadableIds = useCallback(
    () => Array.from(selectedIds).filter((id) => !attachmentOnlyIds.has(id)),
    [selectedIds, attachmentOnlyIds],
  );

  const handleBulkDownloadWord = useCallback(async () => {
    const ids = bulkDownloadableIds();
    if (ids.length === 0) {
      if (selectedIds.size > 0) {
        toast.error(
          "Attachments download with their pack — select the pack instead.",
        );
      }
      return;
    }
    const items = buildBulkItems(ids);
    reportBlockedFills(ids, items);
    if (items.length === 0) return;
    const parts = letterPartsOf(items);
    const pdfForms = items.flatMap((i) => (i.kind === "pdf" ? [i] : []));
    setIsBulkDownloading(true);
    // Determinate progress bar rather than a spinner — the document count is
    // known up front, so show how far along the preparation is (MOR-817).
    const progress = (label: string, completed: number, id?: string | number) =>
      toast(
        <DownloadProgressToast
          label={label}
          completed={completed}
          total={items.length}
        />,
        { id, duration: Infinity },
      );
    const toastId = progress(
      `Preparing ${items.length} document${items.length === 1 ? "" : "s"}…`,
      0,
    );
    try {
      // Bundle each doc's attachments under a per-document subfolder so the
      // same file attached to multiple letters never collides. The toast
      // re-renders per completed document (same id) so the user can see
      // progress instead of a static "preparing" message (MOR-817).
      // Documents are processed a few at a time with shared caches so
      // annexures reused across packs are fetched/rendered once (MOR-971).
      const caches = createBulkDownloadCaches();
      let completedCount = 0;
      const perDocBlobs = await mapWithConcurrency(
        items,
        3,
        async ({ doc }) => {
          const blobs = await gatherDocAttachmentBlobs(doc, caches);
          completedCount += 1;
          progress(doc.name, completedCount, toastId);
          return { doc, blobs };
        },
      );
      const attachments: Array<{ fileName: string; file: Blob }> = [];
      for (const { doc, blobs } of perDocBlobs) {
        const folder = sanitizeFilename(doc.name);
        for (const b of blobs) {
          attachments.push({
            fileName: `${folder}/${b.fileName}`,
            file: b.file,
          });
        }
      }
      // MOR-2342 BKY-09 — PDF forms cannot become Word; they ride in the
      // package as their own PDFs (unique names) so the count is honest.
      const usedPdfNames = new Map<string, number>();
      for (const form of pdfForms) {
        const base = sanitizeFilename(form.doc.name);
        const n = usedPdfNames.get(base) ?? 0;
        usedPdfNames.set(base, n + 1);
        attachments.push({
          fileName: `${n === 0 ? base : `${base}-${n + 1}`}.pdf`,
          file: await fetchPdfBlob(form.url, form.doc.name),
        });
      }
      const summary =
        pdfForms.length > 0
          ? `${parts.length} as Word, ${pdfForms.length} PDF form${pdfForms.length === 1 ? "" : "s"}`
          : null;

      if (attachments.length === 0) {
        progress("Building Word document…", items.length, toastId);
        await downloadBulkAsWord(
          parts,
          `day-one-documents-${items.length}.doc`,
        );
        toast.success("Word document downloaded");
        return;
      }

      if (parts.length === 0) {
        // Only PDF forms were selected: there is no Word document to build.
        progress("Zipping documents…", items.length, toastId);
        const zipBlob = await buildZipArchive(attachments);
        downloadBlob(zipBlob, `day-one-documents-${items.length}.zip`);
        toast.success(
          `Downloaded ${items.length} PDF form${items.length === 1 ? "" : "s"} as ZIP — PDF forms cannot be converted to Word`,
        );
        return;
      }

      progress("Building Word document…", items.length, toastId);
      const combined = await buildBulkWordBlob(
        parts,
        `day-one-documents-${items.length}`,
      );
      progress("Zipping documents…", items.length, toastId);
      const packageBlob = await buildAttachmentDownloadPackage({
        generatedFileName: `day-one-documents-${items.length}.docx`,
        generatedFile: combined,
        attachments,
      });
      downloadBlob(
        packageBlob,
        `day-one-documents-${items.length}-with-attachments.zip`,
      );
      toast.success(
        summary
          ? `Downloaded ${items.length} documents: ${summary} (in the ZIP's attachments folder)`
          : "Word package downloaded with attachments",
      );
    } catch (err) {
      console.error(
        "Failed to bundle attachments with bulk Word download",
        err,
      );
      toast.error(
        "Could not bundle attachments. Downloading the letters on their own.",
      );
      await downloadBulkAsWord(parts, `day-one-documents-${items.length}.doc`);
    } finally {
      toast.dismiss(toastId);
      setIsBulkDownloading(false);
    }
  }, [
    buildBulkItems,
    reportBlockedFills,
    gatherDocAttachmentBlobs,
    bulkDownloadableIds,
    selectedIds,
  ]);

  const handleBulkDownloadPdf = useCallback(
    async (mode: "individual" | "combined") => {
      const ids = bulkDownloadableIds();
      if (ids.length === 0) {
        if (selectedIds.size > 0) {
          toast.error(
            "Attachments download with their pack — select the pack instead.",
          );
        }
        return;
      }
      const items = buildBulkItems(ids);
      reportBlockedFills(ids, items);
      if (items.length === 0) return;
      const parts = letterPartsOf(items);
      setIsBulkDownloading(true);
      // Determinate progress bar rather than a spinner — the document count is
      // known up front, so show how far along the render is (MOR-817).
      const progress = (
        label: string,
        completed: number,
        id?: string | number,
      ) =>
        toast(
          <DownloadProgressToast
            label={label}
            completed={completed}
            total={items.length}
          />,
          { id, duration: Infinity },
        );
      const toastId = progress(
        `Preparing ${items.length} PDF${items.length === 1 ? "" : "s"}…`,
        0,
      );
      try {
        // MOR-847: each selected document becomes its OWN consolidated PDF
        // (letter + its attachments merged in, in order). In "individual" mode
        // the PDFs ship together in a single ZIP; in "combined" mode (MOR-850)
        // they're concatenated into one long PDF. Either way, attachments that
        // can't be merged into a PDF (e.g. .docx, .xlsx) ride alongside in a
        // ZIP under a per-document folder.
        const docPdfs: Array<{ baseName: string; file: Blob }> = [];
        const extraFiles: Array<{ fileName: string; file: Blob }> = [];
        // Two docs can share a display name; suffix repeats so ZIP entries
        // never overwrite each other.
        const usedNames = new Map<string, number>();
        const uniqueName = (base: string) => {
          const count = usedNames.get(base) ?? 0;
          usedNames.set(base, count + 1);
          return count === 0 ? base : `${base}-${count + 1}`;
        };

        // Render each letter concurrently with its attachment gathering, and
        // process a couple of documents at a time so one doc's network waits
        // overlap another's rasterisation. Shared per-run caches mean an
        // annexure attached to several packs is fetched/rendered once
        // (MOR-971). The toast re-renders per completed document (same id) so
        // the user can see how far along the render is (MOR-817).
        const caches = createBulkDownloadCaches();
        let completedCount = 0;
        const renderedDocs = await mapWithConcurrency(
          items,
          2,
          async (item) => {
            const { doc } = item;
            // MOR-2342 BKY-09 — a PDF form is fetched as-is; a letter is
            // rendered. Either becomes one consolidated PDF below.
            const [letterBlob, attachmentBlobs] = await Promise.all([
              item.kind === "letter"
                ? letterPartsToPdfBlob(item.parts, doc.name)
                : fetchPdfBlob(item.url, doc.name),
              gatherDocAttachmentBlobs(doc, caches),
            ]);
            completedCount += 1;
            progress(doc.name, completedCount, toastId);
            return { doc, letterBlob, attachmentBlobs };
          },
        );

        // Consolidate sequentially so `uniqueName` suffixes and the output
        // order stay deterministic (selection order).
        for (const { doc, letterBlob, attachmentBlobs } of renderedDocs) {
          const baseName = uniqueName(sanitizeFilename(doc.name));

          if (attachmentBlobs.length === 0) {
            docPdfs.push({ baseName, file: letterBlob });
            continue;
          }

          const { consolidated, remaining } =
            await consolidateAttachmentsIntoPdf(letterBlob, attachmentBlobs);
          docPdfs.push({ baseName, file: consolidated });
          for (const r of remaining) {
            extraFiles.push({
              fileName: `${baseName}-attachments/${r.fileName}`,
              file: r.file,
            });
          }
        }

        if (mode === "combined") {
          progress(
            `Merging ${items.length} PDF${items.length === 1 ? "" : "s"}…`,
            items.length,
            toastId,
          );
          const combinedPdf = await mergePdfBlobs(docPdfs.map((d) => d.file));
          const combinedName = `day-one-documents-${items.length}.pdf`;
          if (extraFiles.length === 0) {
            downloadBlob(combinedPdf, combinedName);
            toast.success(
              `Downloaded ${items.length} document${items.length === 1 ? "" : "s"} as a combined PDF`,
            );
          } else {
            const zipBlob = await buildZipArchive([
              { fileName: combinedName, file: combinedPdf },
              ...extraFiles,
            ]);
            downloadBlob(zipBlob, `day-one-documents-${items.length}.zip`);
            toast.success(
              "Downloaded combined PDF as ZIP — some attachments couldn't be merged into the PDF",
            );
          }
          return;
        }

        progress(
          `Zipping ${items.length} PDF${items.length === 1 ? "" : "s"}…`,
          items.length,
          toastId,
        );
        const zipBlob = await buildZipArchive([
          ...docPdfs.map((d) => ({
            fileName: `${d.baseName}.pdf`,
            file: d.file,
          })),
          ...extraFiles,
        ]);
        downloadBlob(zipBlob, `day-one-documents-${items.length}.zip`);
        toast.success(
          `Downloaded ${items.length} PDF${items.length === 1 ? "" : "s"} as ZIP`,
        );
      } catch (err) {
        console.error("Failed to build PDFs for bulk download", err);
        toast.error(
          "Could not prepare the PDFs. Downloading the letters as a combined PDF instead.",
        );
        downloadBulkAsPdf(parts, `${items.length} Generated Documents`);
      } finally {
        toast.dismiss(toastId);
        setIsBulkDownloading(false);
      }
    },
    [
      buildBulkItems,
      reportBlockedFills,
      gatherDocAttachmentBlobs,
      bulkDownloadableIds,
      selectedIds,
    ],
  );

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner className="size-6" />
      </div>
    );
  }

  if (!visiblePrecedents || visiblePrecedents.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 bg-muted">
        <FileText className="size-8 text-primary/20" />
        <p className="text-sm text-primary/40">No documents generated yet.</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            navigate({
              to: "/dashboard/jobs/$jobId/generate-precedents",
              params: { jobId },
            })
          }
        >
          <Plus className="mr-1.5 size-3.5" />
          Generate Precedents
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-4 bg-muted p-6">
      {/* Header + stats */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-primary">
            Document Generator
          </h2>
          <div className="mt-1 flex items-center gap-3 text-xs text-primary/50">
            <span>
              {stats.total} {stats.total === 1 ? "document" : "documents"}
            </span>
            {stats.attachments > 0 && (
              <span>
                {stats.attachments} attachment
                {stats.attachments === 1 ? "" : "s"}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Bulk actions bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 rounded-lg border border-border bg-secondary/30 px-4 py-2">
          <span className="text-xs font-medium text-primary/60">
            {selectedIds.size} selected
          </span>
          <button
            type="button"
            onClick={handleSendWithMorgan}
            disabled={jobMailbox?.status !== "active"}
            title={
              jobMailbox?.status === "active"
                ? "Morgan drafts a cover email per document for your approval"
                : "Enable the job mailbox on the Mail page first"
            }
            className="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-primary hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Sparkles className="size-3" />
            Generate draft email
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                disabled={isBulkDownloading}
                className="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-primary/60 hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isBulkDownloading ? (
                  <Loader2 className="size-3 animate-spin" />
                ) : (
                  <Download className="size-3" />
                )}
                {isBulkDownloading ? "Preparing…" : "Download"}
                {!isBulkDownloading && <ChevronDown className="size-3" />}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              onCloseAutoFocus={(e) => e.preventDefault()}
            >
              <DropdownMenuItem
                disabled={isBulkDownloading}
                onClick={handleBulkDownloadWord}
              >
                <FileDown className="mr-2 size-3.5" />
                Download as Word
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={isBulkDownloading}
                onClick={() => handleBulkDownloadPdf("individual")}
              >
                <FileText className="mr-2 size-3.5" />
                Download as individual PDFs
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={isBulkDownloading}
                onClick={() => handleBulkDownloadPdf("combined")}
              >
                <FileText className="mr-2 size-3.5" />
                Download as combined PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-rose-600 hover:bg-rose-50"
              >
                <Trash2 className="size-3" />
                Delete
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  Delete {selectedIds.size} document
                  {selectedIds.size === 1 ? "" : "s"}?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently remove the selected document
                  {selectedIds.size === 1 ? "" : "s"} from this job. This action
                  cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => handleBulkDelete()}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <button
            type="button"
            onClick={() => setSelectedIds(new Set())}
            className="ml-auto text-xs text-primary/40 hover:text-primary/60"
          >
            Clear
          </button>
        </div>
      )}

      {/* Document data table — search, filter, group-by, sort */}
      <DayOneDataTable
        jobId={jobId}
        rows={tableRows}
        selectedIds={selectedIds}
        onSelectedIdsChange={setSelectedIds}
        meta={tableMeta}
        onRowClick={(row) => openDayOneRow(row)}
        toolbarActions={
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSharedAttachmentsOpen(true)}
            >
              <Paperclip className="mr-1.5 size-3.5" />
              Shared Attachments
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate({ to: "/dashboard/settings/docs" })}
            >
              <Settings className="mr-1.5 size-3.5" />
              Precedent Settings
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                navigate({
                  to: "/dashboard/jobs/$jobId/generate-precedents",
                  params: { jobId },
                  search: { reselect: true },
                })
              }
            >
              <RefreshCw className="mr-1.5 size-3.5" />
              Edit Fields
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                navigate({
                  to: "/dashboard/jobs/$jobId/generate-precedents",
                  params: { jobId },
                })
              }
            >
              <Plus className="mr-1.5 size-3.5" />
              Add More
            </Button>
          </>
        }
      />

      {/* Preview dialog — lifted from the inline DocumentRow so the
          table component stays purely presentational. Mounted only when
          a row is selected so the heavy editor / fields panel inside
          the dialog isn't paid for upfront. */}
      {previewDoc && (
        <DayOnePreviewDialog
          doc={previewDoc}
          brandingProfile={brandingProfile}
          dbTemplate={templates?.find(
            (t) => t.templateKey === previewDoc.templateKey,
          )}
          job={job}
          open={!!previewDocId}
          onOpenChange={(open) => {
            if (!open) setPreviewDocId(null);
          }}
          allPresets={allPresets}
          favouriteIds={favouriteIds}
          orgId={orgId as Id<"organizations">}
          practitionerOptions={practitionerOptions}
          presetPractitionerOptions={presetPractitionerOptions}
          signatureData={currentUser ?? null}
          resolvedVariants={resolveVariantsForDoc(previewDoc)}
          allVariants={letterheadVariantsRaw ?? []}
          allBundles={bundleNames}
          buildHtmlLetterAttachmentParts={() =>
            buildHtmlLetterAttachmentPartsForDoc(previewDoc)
          }
          siblingPrecedents={(visiblePrecedents ?? []).map((p) => ({
            _id: p._id,
            templateKey: p.templateKey,
            name: p.name,
          }))}
          onSetLetterheadOverride={async (override) => {
            try {
              await setLetterheadOverride({
                precedentId: previewDoc._id,
                override,
              });
            } catch (err) {
              toast.error(
                extractErrorMessage(
                  err,
                  "Failed to update letterhead override",
                ),
              );
            }
          }}
          variantGroups={variantGroupsForEditor}
          onSetVariantSelection={async (slug, value) => {
            try {
              const next = {
                ...(previewDoc.variantSelections ?? {}),
                [slug]: value,
              };
              await setVariantSelectionsMut({
                precedentId: previewDoc._id,
                variantSelections: next,
              });
            } catch (err) {
              toast.error(extractErrorMessage(err, "Failed to update variant"));
            }
          }}
          onClearVariantSelection={async (slug) => {
            try {
              const next = { ...(previewDoc.variantSelections ?? {}) };
              delete next[slug];
              await setVariantSelectionsMut({
                precedentId: previewDoc._id,
                variantSelections: next,
              });
            } catch (err) {
              toast.error(extractErrorMessage(err, "Failed to reset variant"));
            }
          }}
          onSetSnippetSelection={async (slug, snippetId) => {
            try {
              // Boundary: the schema stores selections as plain strings and
              // the picker callback hands back a string, but the mutation arg
              // is branded — the values are genuine variantSnippets ids.
              const next: Record<string, Id<"variantSnippets">> = {
                ...((previewDoc.snippetSelections ?? {}) as Record<
                  string,
                  Id<"variantSnippets">
                >),
                [slug]: snippetId as Id<"variantSnippets">,
              };
              await setSnippetSelectionsMut({
                precedentId: previewDoc._id,
                snippetSelections: next,
              });
            } catch (err) {
              toast.error(
                extractErrorMessage(err, "Failed to update snippet selection"),
              );
            }
          }}
          onClearSnippetSelection={async (key) => {
            try {
              const next = { ...(previewDoc.snippetSelections ?? {}) };
              delete next[key];
              await setSnippetSelectionsMut({
                precedentId: previewDoc._id,
                snippetSelections: Object.keys(next).length ? next : null,
              });
            } catch (err) {
              toast.error(
                extractErrorMessage(err, "Failed to reset snippet selection"),
              );
            }
          }}
        />
      )}

      <SharedAttachmentsDialog
        jobId={jobId as Id<"jobs">}
        open={sharedAttachmentsOpen}
        onOpenChange={setSharedAttachmentsOpen}
      />

      {/* Contact detail slide-outs — opened by clicking a contact in the
          table's Contact column. */}
      <ContactDetailPanel
        jobId={jobId as Id<"jobs">}
        contactId={viewingJobContactId}
        open={viewingJobContactId !== null}
        onClose={() => setViewingJobContactId(null)}
        jobContacts={jobContacts ?? []}
      />
      <FirmContactDetailPanel
        firmContactId={viewingFirmContactId}
        open={viewingFirmContactId !== null}
        canEdit={activeOrg?.role === "owner" || activeOrg?.role === "admin"}
        onClose={() => setViewingFirmContactId(null)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Preview Dialog — controlled by parent
//
// The previously-inline `DocumentRow` has been replaced by the
// `DayOneDataTable` component (see `src/components/jobs/phases/day-one/`).
// The dialog below is now mounted directly by `DayOnePage` and driven by
// the `previewDocId` state set when a row is clicked.
// ---------------------------------------------------------------------------

type SignatureData = {
  signatureText?: string;
  signatureImageUrl?: string;
  signatureScale?: number;
} | null;

function DayOnePreviewDialog({
  doc,
  brandingProfile,
  dbTemplate,
  job,
  open,
  onOpenChange,
  allPresets,
  favouriteIds,
  orgId,
  practitionerOptions,
  presetPractitionerOptions,
  signatureData,
  resolvedVariants,
  allVariants,
  allBundles,
  onSetLetterheadOverride,
  variantGroups,
  onSetVariantSelection,
  onClearVariantSelection,
  onSetSnippetSelection,
  onClearSnippetSelection,
  buildHtmlLetterAttachmentParts,
  siblingPrecedents,
}: {
  doc: DayOneDoc;
  brandingProfile: BrandingProfile | null | undefined;
  dbTemplate: TemplateDoc | undefined;
  job: JobData | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  siblingPrecedents: Array<{
    _id: Id<"jobPrecedents">;
    templateKey: string;
    name: string;
  }>;
  allPresets: Doc<"fieldPresets">[] | undefined;
  favouriteIds: Id<"fieldPresets">[] | undefined;
  orgId: Id<"organizations">;
  practitionerOptions: string[];
  /**
   * Registered practitioners from the firm's Practitioner Details presets —
   * the only valid joint appointees.
   */
  presetPractitionerOptions: string[];
  signatureData: SignatureData;
  resolvedVariants: VariantFragments[];
  allVariants: ResolvedVariant[];
  allBundles: string[];
  onSetLetterheadOverride: (
    override: LetterheadOverride | null,
  ) => void | Promise<void>;
  variantGroups?: Array<{
    slug: string;
    name: string;
    options: Array<{ value: string; label: string }>;
  }>;
  onSetVariantSelection?: (
    groupSlug: string,
    optionValue: string,
  ) => void | Promise<void>;
  onClearVariantSelection?: (groupSlug: string) => void | Promise<void>;
  onSetSnippetSelection?: (
    keyOrSlug: string,
    snippetId: string,
  ) => void | Promise<void>;
  onClearSnippetSelection?: (key: string) => void | Promise<void>;
  /**
   * Forwarded to the dialog's `DocumentDownloadMenu` so HTML-letter
   * attachments (which can't be fetched from storage) get rendered into
   * the export. See {@link buildHtmlLetterAttachmentPartsForDoc}.
   */
  buildHtmlLetterAttachmentParts?: () => Array<{
    name: string;
    parts: LetterParts;
  }>;
}) {
  // Include the variant tier: a letterhead variant can carry its own
  // margins (Beacon does), and the export pipeline honours them — the
  // preview must resolve with the same inputs or the two drift.
  const docPadding = resolveDocumentPadding(
    brandingProfile,
    dbTemplate,
    doc,
    doc.isThirdPartyForm ? [] : variantPaddingSources(resolvedVariants),
  );
  const [editing, setEditing] = useState(false);
  const [wysiwygEditing, setWysiwygEditing] = useState(false);
  const wysiwygEditorRef = useRef<TemplateEditorHandle>(null);
  const [editedValues, setEditedValues] = useState<Record<string, string>>({});
  const editedValuesRef = useRef(editedValues);
  useEffect(() => {
    editedValuesRef.current = editedValues;
  }, [editedValues]);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [presetSelections, setPresetSelections] = useState<
    Record<string, Id<"fieldPresets">>
  >({});
  const [editingAttachments, setEditingAttachments] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);

  const favouriteSet = useMemo(
    () => new Set(favouriteIds ?? []),
    [favouriteIds],
  );

  const presetsByCategory = useMemo(() => {
    const map = new Map<string, Array<Doc<"fieldPresets">>>();
    if (!allPresets) return map;
    for (const p of allPresets) {
      const list = map.get(p.categoryId) ?? [];
      list.push(p);
      map.set(p.categoryId, list);
    }
    return map;
  }, [allPresets]);

  // Org-defined custom fields. Surfaced in the Edit Fields panel so any
  // custom keys (e.g. PPSR Registration Number) saved on the precedent get
  // their proper label + category, even when the template's `extractedFields`
  // hasn't been refreshed since the field was inserted (MOR-299).
  const { data: customFieldDefs } = useQuery({
    ...convexQuery(api.settings.precedents.customFields.listByOrg, { orgId }),
    enabled: !!orgId,
  });

  const { data: variantGroupsData } = useQuery({
    ...convexQuery(api.settings.precedents.variantGroups.listVariantGroups, {
      orgId,
    }),
    enabled: !!orgId,
  });
  const variantGroupsForEditor = useMemo(
    () =>
      (variantGroupsData ?? []).map((g) => ({
        slug: g.slug,
        name: g.name,
        options: g.options,
      })),
    [variantGroupsData],
  );

  const { data: variantSnippetsData } = useQuery({
    ...convexQuery(api.settings.precedents.variantSnippets.listSnippets, {
      orgId,
    }),
    enabled: !!orgId,
  });
  const variantSnippetsForEditor = useMemo(
    () =>
      (variantSnippetsData ?? []).map((s) => ({
        _id: String(s._id),
        name: s.name,
        contentHtml: s.contentHtml,
        groupSlug: s.groupSlug,
        optionValue: s.optionValue,
        slotLabel: s.slotLabel,
      })),
    [variantSnippetsData],
  );

  const customFieldByKey = useMemo(() => {
    const map = new Map<
      string,
      { key: string; label: string; fieldType: string; categoryId: string }
    >();
    if (!customFieldDefs) return map;
    for (const def of customFieldDefs) {
      map.set(def.key, {
        key: def.key,
        label: def.label,
        fieldType: def.fieldType,
        categoryId: def.categoryId,
      });
    }
    return map;
  }, [customFieldDefs]);

  const customFieldKeys = useMemo(
    () => new Set(customFieldByKey.keys()),
    [customFieldByKey],
  );

  // Firm-contact custom field definitions — used to give `contact_firm_cf_*`
  // keys a readable label and the right input type in the Edit Fields panel.
  const { data: firmContactFieldDefs } = useQuery({
    ...convexQuery(
      api.contacts.fieldDefinitions.listFirmContactFieldDefinitions,
      { orgId },
    ),
    enabled: !!orgId,
  });
  const firmContactCustomFieldByKey = useMemo(() => {
    const map = new Map<
      string,
      { label: string; fieldType: string; category: FirmContactCategory }
    >();
    for (const def of firmContactFieldDefs ?? []) {
      // Same slug can exist in multiple categories; first definition wins
      // for label purposes (the value map is category-agnostic at render).
      if (!map.has(def.key)) {
        map.set(def.key, {
          label: def.label,
          fieldType: def.fieldType,
          category: def.category,
        });
      }
    }
    return map;
  }, [firmContactFieldDefs]);

  const orgPresetKeys = useMemo(() => {
    const keys = new Set<string>();
    if (!allPresets) return keys;
    for (const p of allPresets) {
      for (const k of Object.keys(p.values)) keys.add(k);
    }
    return keys;
  }, [allPresets]);

  const templateFieldsWithExample = useMemo(() => {
    const set = new Set<string>();
    const fields: Array<{ key: string; exampleValue?: string }> | undefined =
      dbTemplate?.extractedFields;
    if (!fields) return set;
    for (const f of fields) {
      if (!f.exampleValue?.trim()) continue;
      set.add(f.key);
    }
    return set;
  }, [dbTemplate?.extractedFields]);

  const classifySource = useCallback(
    (key: string): FieldSource =>
      classifyFieldSource({
        key,
        customFieldKeys,
        orgPresetKeys,
        templateFieldsWithExample,
      }),
    [customFieldKeys, orgPresetKeys, templateFieldsWithExample],
  );

  const { mutateAsync: updateFieldValues } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.precedents.mutations.updateFieldValues,
    ),
  });

  // Source-link picker support — only relevant when this doc was
  // generated from a job-contact-source fan-out (i.e. has both
  // `jobContactId` and `contactSourceLinkKind` stamped on it). Pulls
  // the full source row set for the linked contact so the picker can
  // list sibling links of the same kind for last-minute swapping.
  const { data: docSourceRows } = useQuery({
    ...convexQuery(api.contacts.queries.getJobContactSourceRows, {
      jobId: doc.jobId,
      jobContactId: doc.jobContactId as Id<"jobContacts">,
    }),
    enabled: Boolean(doc.jobContactId && doc.contactSourceLinkKind),
  });

  const { mutateAsync: updateSourceLink } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.precedents.mutations.updateJobPrecedentSourceLink,
    ),
  });

  const handleSwapSourceLink = useCallback(
    async (sourceKind: string, sourceId: string) => {
      try {
        await updateSourceLink({
          precedentId: doc._id,
          sourceKind: sourceKind as
            | "security_interest"
            | "liability"
            | "loan"
            | "employee"
            | "ato_sro"
            | "corporate"
            | "related_party"
            | "asset_valuation",
          sourceId,
        });
        toast.success("Source link updated — fields re-resolved");
      } catch (err) {
        toast.error(extractErrorMessage(err, "Failed to update source link"));
      }
    },
    [updateSourceLink, doc._id],
  );

  const { mutateAsync: updateEditedHtml } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.precedents.mutations.updateEditedHtml,
    ),
  });
  const { mutateAsync: setDocumentPageMarginOverride } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.precedents.mutations.setDocumentPageMarginOverride,
    ),
  });

  // Autosave Edit Fields panel — flushes on field blur and preset selection.
  // Baseline is reset whenever the dialog opens (see open useEffect below).
  const fieldsAutosave = useAutosave<Record<string, string>>({
    getValue: () => editedValuesRef.current,
    initialValue: doc.fieldValues ?? {},
    save: async (values) => {
      await updateFieldValues({
        precedentId: doc._id,
        fieldValues: values,
      });
    },
  });

  const { mutateAsync: toggleFavourite } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.favourites.toggleFavourite,
    ),
  });

  /**
   * Toggle the checked state of a checkbox placement on the filled PDF.
   * Resolves the right field-values patch for each placement mode:
   *
   * - **Group member** (`groupKey` + `optionValue`): sets the group key to
   *   this option's value. Clicking the currently-selected option clears
   *   the group (toggle off).
   * - **Canonical-linked** (legacy yes/no): flips the bound boolean
   *   field. `checkboxValue: "unchecked"` inverts the click semantics so
   *   a "No" box flips the field to false.
   * - **Standalone** (no canonical, no group): records the override
   *   against the synthetic key `__cb__:<placementId>` so the fill
   *   action can pick it up regardless of any default.
   *
   * The new values are merged into the doc's saved fieldValues and
   * persisted via `updateFieldValues`, which re-triggers the fill action
   * so the preview reloads with the new tick state.
   */
  const togglePdfCheckbox = useCallback(
    async (fp: {
      id?: string;
      canonicalKey?: string;
      checkboxValue?: string;
      groupKey?: string;
      optionValue?: string;
      defaultChecked?: boolean;
    }) => {
      // Read from the in-memory ref (latest local edits) rather than the
      // query-backed `doc.fieldValues` snapshot captured at callback
      // creation. Otherwise rapid clicks or a recent left-panel edit can
      // be overwritten by a stale closure.
      const baseline = editedValuesRef.current ?? doc.fieldValues ?? {};
      const current: Record<string, string> = { ...baseline };
      const placementKey = fp.id ? `__cb__:${fp.id}` : null;

      if (fp.groupKey) {
        const currentVal = (current[fp.groupKey] ?? "").trim();
        const target = (fp.optionValue ?? "").trim();
        if (!target) return;
        current[fp.groupKey] =
          currentVal.toLowerCase() === target.toLowerCase() ? "" : target;
      } else if (fp.canonicalKey) {
        const raw = (current[fp.canonicalKey] ?? "").trim().toLowerCase();
        const fieldChecked = raw === "true" || raw === "yes" || raw === "1";
        const boxIsTicked =
          fp.checkboxValue === "unchecked" ? !fieldChecked : fieldChecked;
        const nextFieldChecked =
          fp.checkboxValue === "unchecked" ? boxIsTicked : !boxIsTicked;
        current[fp.canonicalKey] = nextFieldChecked ? "true" : "false";
      } else if (placementKey) {
        const raw = (current[placementKey] ?? "").toLowerCase();
        const explicit =
          raw === "true" || raw === "1" || raw === "yes"
            ? true
            : raw === "false" || raw === "0" || raw === "no"
              ? false
              : null;
        const currentTicked = explicit ?? !!fp.defaultChecked;
        current[placementKey] = currentTicked ? "false" : "true";
      } else {
        return;
      }

      // Mirror the write into the left-panel state and the autosave
      // baseline. Without this, the next blur-driven flush reads the
      // pre-click `editedValues` and reverts this click on the backend.
      setEditedValues(current);
      editedValuesRef.current = current;
      fieldsAutosave.resetBaseline(current);

      try {
        await updateFieldValues({
          precedentId: doc._id,
          fieldValues: current,
        });
      } catch (e) {
        console.warn("[togglePdfCheckbox] updateFieldValues failed", e);
      }
    },
    [doc._id, doc.fieldValues, updateFieldValues, fieldsAutosave],
  );

  // Reset edit state when doc changes or dialog opens
  useEffect(() => {
    if (open) {
      const initial = { ...(doc.fieldValues ?? {}) };
      setEditedValues(initial);
      editedValuesRef.current = initial;
      fieldsAutosave.resetBaseline(initial);
      setEditing(false);
      setWysiwygEditing(false);
      setFocusedField(null);

      // Match current doc values to presets for auto-selection
      if (allPresets) {
        const selections: Record<string, Id<"fieldPresets">> = {};
        const docValues = doc.fieldValues ?? {};
        for (const catId of DEFAULT_CATEGORIES) {
          const catPresets = allPresets.filter((p) => p.categoryId === catId);
          const category = FIELD_CATEGORIES.find((c) => c.id === catId);
          if (!category || catPresets.length === 0) continue;
          for (const preset of catPresets) {
            const pv = preset.values;
            const hasValues = category.fields.some(
              (f) => (pv[f.key] ?? "") !== "",
            );
            const allMatch =
              hasValues &&
              category.fields.every(
                (f) => (pv[f.key] ?? "") === (docValues[f.key] ?? ""),
              );
            if (allMatch) {
              selections[catId] = preset._id;
              break;
            }
          }
        }
        setPresetSelections(selections);
      } else {
        setPresetSelections({});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- this effect (re)initialises the edit session, snapshotting doc.fieldValues/allPresets at dialog-open (or doc switch) only. Re-running on doc.fieldValues would wipe in-progress edits on every reactive re-emit (the effect below merges those field-by-field, MOR-458); re-running on allPresets would clobber the user's manual preset picks; fieldsAutosave is a new object each render (its resetBaseline is identity-stable).
  }, [open, doc._id]);

  // Sync editedValues from doc.fieldValues whenever the reactive query
  // emits a new snapshot (e.g. after togglePdfCheckbox or any external
  // mutation). Without this, `editedValues` only initialises on dialog
  // open, and the autosave eventually flushes the stale snapshot,
  // overwriting clicks made directly on the PDF preview (MOR-458).
  // Skip the currently-focused field so a typing user isn't stomped.
  // Only writes state when something actually changed — otherwise every
  // reactive re-emit would trigger resetBaseline → setStatus/setLastSavedAt
  // re-renders and the whole dialog feels laggy.
  useEffect(() => {
    if (!open) return;
    const incoming = doc.fieldValues ?? {};
    const focused = focusedField;
    const prev = editedValuesRef.current;
    const next: Record<string, string> = { ...prev };
    for (const [key, value] of Object.entries(incoming)) {
      if (key === focused) continue;
      if (typeof value !== "string") continue;
      if (prev[key] !== value) {
        next[key] = value;
      }
    }
    const changed = Object.keys(next).some((key) => next[key] !== prev[key]);
    if (!changed) return;
    editedValuesRef.current = next;
    setEditedValues(next);
    fieldsAutosave.resetBaseline(incoming);
    // focusedField is intentionally read but not in deps — we don't want
    // refocusing to re-trigger a sync. The next doc.fieldValues update
    // will pick up any keys that were skipped while focused.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, doc.fieldValues]);

  const baseValues = useMemo(
    () => (editing ? editedValues : (doc.fieldValues ?? {})),
    [editing, editedValues, doc.fieldValues],
  );
  const previewValues = useMemo(() => {
    if (doc.templateKey !== "form_gallagher") return baseValues;

    const jobValues = job ? mapJobToCanonicalFields(job) : {};
    // The doc's saved field values are treated as analyst overrides so they
    // always win over canonical-source resolution. This is essential for
    // Gallagher fields whose configured sourceKey differs from fieldKey
    // (e.g. business_industry → industry_division): without this, anything
    // the analyst typed into 8a via Edit Fields gets silently overwritten.
    const savedValues = Object.fromEntries(
      Object.entries(baseValues).filter(([, value]) => value !== ""),
    ) as Record<string, string>;

    const config: GallagherFieldConfig[] | undefined = dbTemplate?.fieldConfig;
    const resolved = resolveGallagherFieldValues({
      baseValues: jobValues,
      overrides: savedValues,
      config,
    });

    // Belt-and-braces: also expose the canonical source values that the
    // payload builder uses as fallbacks (e.g. industry_division for
    // OccupationOfAppointment, registered_office_street for
    // PrincipalRiskAddress). Without this the payload's `valueFor(...,
    // fallbackKey)` chain has nothing to fall back to because the resolver
    // only outputs Gallagher field keys. Hidden fields are respected by
    // skipping any source key that backs a hidden Gallagher field.
    const hiddenSourceKeys = new Set(
      (config ?? [])
        .filter(
          (entry) =>
            entry.hidden && entry.sourceType === "canonical" && entry.sourceKey,
        )
        .map((entry) => entry.sourceKey as string),
    );
    const sourceFallbacks: Record<string, string> = {};
    for (const sourceKey of GALLAGHER_SOURCE_FIELD_KEYS) {
      if (hiddenSourceKeys.has(sourceKey)) continue;
      const value = savedValues[sourceKey] ?? jobValues[sourceKey];
      if (value) sourceFallbacks[sourceKey] = value;
    }

    return { ...sourceFallbacks, ...resolved };
  }, [baseValues, dbTemplate?.fieldConfig, doc.templateKey, job]);
  const activeValues = enrichWithSignature(previewValues, signatureData);

  // One preview row per variant block in this document, resolved for the
  // currently-selected option (job default ← per-doc override). The preview
  // renders field placeholders as chips exactly like the document body
  // (canonical labels + source colours), not raw {{key}} text.
  const variantBlocksForPicker = useMemo(() => {
    const html = dbTemplate?.templateHtml as string | undefined;
    const instances = extractVariantBlockInstances(html).filter((b) => b.vbId);
    const mergedSel = mergeVariantSelections(
      job?.variantSelections,
      doc.variantSelections,
    );
    const byId = new Map(variantSnippetsForEditor.map((s) => [s._id, s]));
    const slotFromRefs = (refs: Record<string, string | null>) => {
      for (const id of Object.values(refs)) {
        const s = id ? byId.get(id) : undefined;
        if (s) return s.slotLabel || s.name;
      }
      return undefined;
    };
    const docSnips = doc.snippetSelections ?? {};
    const checkboxKeys = collectCheckboxKeys(dbTemplate);
    const dateKeys = collectDateKeys(dbTemplate);
    return instances.map((b) => {
      const vbId = b.vbId as string;
      const selectedOption = mergedSel?.[b.groupSlug];
      const overrideId = docSnips[vbId];
      const effId =
        overrideId ??
        (selectedOption
          ? (b.snippetRefs[selectedOption] ?? undefined)
          : undefined);
      const snip = effId ? byId.get(effId) : undefined;
      return {
        vbId,
        groupSlug: b.groupSlug,
        slotLabel: slotFromRefs(b.snippetRefs),
        previewHtml: snip
          ? replaceFieldPlaceholders(
              stripInlineHighlights(snip.contentHtml),
              activeValues,
              {
                editing: true,
                classifySource,
                checkboxKeys,
                dateKeys,
                collectionData: doc.collectionData,
              },
            )
          : undefined,
      };
    });
  }, [
    dbTemplate,
    job?.variantSelections,
    doc.variantSelections,
    doc.snippetSelections,
    variantSnippetsForEditor,
    activeValues,
    classifySource,
    doc.collectionData,
  ]);

  const bodyHtml = dbTemplate?.templateHtml;

  // LetterParts for the preview's pagination engine. Mirrors the per-doc
  // shape `buildBulkItems` builds for export (variants + padding +
  // font/spacing defaults) so the preview paginates with the SAME inputs as
  // the PDF. `bodyHtml` is left empty here — the body actually rendered &
  // measured is passed via the `bodyHtml` prop below (which carries editing
  // chips while editing); the engine only reads variants/padding from parts.
  const previewParts = useMemo<LetterParts>(
    () => ({
      bodyHtml: "",
      variants: doc.isThirdPartyForm ? [] : resolvedVariants,
      brandingPadding: !doc.isThirdPartyForm ? brandingProfile : undefined,
      templatePadding: !doc.isThirdPartyForm ? dbTemplate : undefined,
      documentPadding: !doc.isThirdPartyForm ? doc : undefined,
      templatePageOverrides: !doc.isThirdPartyForm
        ? (dbTemplate?.pageMarginOverrides ?? null)
        : null,
      documentPageOverrides: !doc.isThirdPartyForm
        ? (doc.pageMarginOverrides ?? null)
        : null,
      defaultFontFamily: !doc.isThirdPartyForm
        ? (brandingProfile?.editorFontFamily ?? undefined)
        : undefined,
      defaultLineHeight: !doc.isThirdPartyForm
        ? (brandingProfile?.editorLineHeight ?? undefined)
        : undefined,
      bodyTextColor: !doc.isThirdPartyForm
        ? (brandingProfile?.bodyTextColor ?? undefined)
        : undefined,
      defaultFontSize: !doc.isThirdPartyForm
        ? (brandingProfile?.editorFontSize ?? undefined)
        : undefined,
      defaultParagraphSpacingBefore: !doc.isThirdPartyForm
        ? (brandingProfile?.editorParagraphSpacingBefore ?? undefined)
        : undefined,
      defaultParagraphSpacingAfter: !doc.isThirdPartyForm
        ? (brandingProfile?.editorParagraphSpacingAfter ?? undefined)
        : undefined,
      pageNumbering: !doc.isThirdPartyForm
        ? resolvePageNumbering(brandingProfile, dbTemplate)
        : null,
    }),
    [doc, resolvedVariants, brandingProfile, dbTemplate],
  );

  const isGallagherDoc = doc.templateKey === "form_gallagher";
  const hasFilledPdf = !!doc.filledPdfUrl && !isGallagherDoc;
  const hasPdfMappings = !!dbTemplate?.pdfFieldMappings?.length;
  const hasFieldPositions = !!dbTemplate?.fieldPositions?.length;

  // Determine editable fields for the edit panel.
  //
  // A PDF form template can have fields described in any of three places:
  //   1. `fieldPositions` — modern visual editor (each entry has canonicalKey)
  //   2. `pdfFieldMappings` — legacy AcroForm name-based mapping
  //   3. `extractedFields` — letter templates with {{placeholder}} extraction
  //
  // We collect from all of them and dedupe by key downstream so a template
  // with positions-only (e.g. ATO Incap placed visually) still surfaces every
  // field in the side panel. Without this, the "Edit Fields" panel renders
  // empty for those forms even though the button is visible.
  const titleCase = (key: string) =>
    key.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase());

  // This template's own field labels, used for empty placeholder chips in the
  // preview so they match the Edit Fields inputs and the library preview.
  const templateFieldLabels = useMemo(
    () => buildFieldsLabelMap(dbTemplate?.extractedFields),
    [dbTemplate?.extractedFields],
  );

  const editableFields = useMemo(() => {
    type DerivedField = {
      key: string;
      label: string;
      description?: string;
      fieldType: string;
      categoryId: string;
      options?: string[];
    };
    const collected: Array<DerivedField> = [];
    const seenKeys = new Set<string>();

    const pushIfNew = (field: DerivedField) => {
      if (seenKeys.has(field.key)) return;
      seenKeys.add(field.key);
      collected.push(field);
    };

    // Resolution order for any key: canonical → org custom def → fallback.
    // `hint` carries the template's own metadata for the key and its label
    // wins over the built-in one, so the input is named after the placeholder
    // the user sees in the document (see `preferTemplateFieldLabel`).
    const resolveField = (
      key: string,
      hint?: { label?: string; description?: string; fieldType?: string },
    ): DerivedField => {
      const canonical = CANONICAL_FIELDS_MAP[key];
      if (canonical) {
        return preferTemplateFieldLabel(
          {
            key,
            label: canonical.label,
            description: canonical.description,
            fieldType: canonical.fieldType,
            categoryId: canonical.categoryId,
          },
          hint,
        );
      }
      const custom = customFieldByKey.get(key);
      if (custom) {
        return preferTemplateFieldLabel(
          {
            key,
            label: custom.label,
            fieldType: custom.fieldType,
            categoryId: custom.categoryId,
          },
          hint,
        );
      }
      // Contact-source field — render a proper `Job Contact: <Source>:
      // <Attribute>` label using the registry rather than titleCase'ing
      // the underscore-joined key into something illegible.
      const sourceBinding = parseContactSourceFieldKey(key);
      if (sourceBinding) {
        const meta = CONTACT_SOURCE_FIELDS[sourceBinding.sourceKind].find(
          (p) => p.part === sourceBinding.part,
        );
        const sourceLabel =
          CONTACT_SOURCE_KIND_LABELS[sourceBinding.sourceKind];
        const partLabel = meta?.label ?? titleCase(sourceBinding.part);
        return {
          key,
          label: `Job Contact: ${sourceLabel}: ${partLabel}`,
          fieldType: hint?.fieldType ?? "text",
          categoryId: "contact_source",
        };
      }
      // Firm-contact custom field — render `Firm Contact (Category) —
      // Label` using the org's definitions rather than a raw slug.
      const customBinding = parseContactCustomFieldKey(key);
      if (customBinding) {
        const def = firmContactCustomFieldByKey.get(customBinding.key);
        const domainLabel =
          customBinding.domain === "firm" ? "Firm Contact" : "Job Contact";
        const scoped = def
          ? `${domainLabel} (${FIRM_CATEGORY_META[def.category].label})`
          : domainLabel;
        return {
          key,
          label: `${scoped} — ${def?.label ?? titleCase(customBinding.key)}`,
          fieldType: hint?.fieldType ?? def?.fieldType ?? "text",
          categoryId: "contact_custom",
        };
      }
      return {
        key,
        label: hint?.label ?? titleCase(key),
        description: hint?.description,
        fieldType: hint?.fieldType ?? "text",
        categoryId: "other",
      };
    };

    // Build the set of keys actually referenced by this document so we
    // only surface fields the user can see in the preview.
    // MOR-790 — PDF-overlay templates (same condition as the fill dispatch
    // in convex/jobs/precedents/mutations.ts) render exclusively from
    // `fieldPositions` / `pdfFieldMappings`; their `templateHtml` and
    // `extractedContent` are extraction artefacts. Scanning them here would
    // resurrect stale extracted fields (ghosts) that have no bearing on the
    // filled PDF, so only the user's own `editedHtml` is considered.
    const isPdfFillTemplate =
      dbTemplate?.templateType !== "insurance" &&
      dbTemplate?.templateKey !== "form_gallagher" &&
      Boolean(
        (dbTemplate?.fieldPositions as unknown[] | undefined)?.length ||
        (dbTemplate?.pdfFieldMappings as unknown[] | undefined)?.length,
      );
    const bodyKeys = collectPlaceholderKeysFromStoredTemplate(
      isPdfFillTemplate
        ? (doc.editedHtml ?? "")
        : `${dbTemplate?.templateHtml ?? ""}\n${doc.editedHtml ?? ""}`,
      isPdfFillTemplate ? undefined : dbTemplate?.extractedContent,
    );

    const positionKeys = new Set<string>();
    // Checkbox group placements share a `groupKey` and collectively
    // represent one single-select question. Collect a synthesized
    // "select"-typed field per group so the Edit Fields panel surfaces
    // them with the right options.
    const groupBundles = new Map<
      string,
      { label: string; options: string[] }
    >();
    if (dbTemplate?.fieldPositions?.length) {
      for (const fp of dbTemplate.fieldPositions) {
        if (fp.canonicalKey) positionKeys.add(fp.canonicalKey);
        if (fp.groupKey && fp.fieldType === "checkbox") {
          const entry =
            groupBundles.get(fp.groupKey) ??
            (() => {
              const created = {
                label: fp.groupLabel ?? fp.groupKey,
                options: [],
              };
              groupBundles.set(fp.groupKey, created);
              return created;
            })();
          if (fp.groupLabel) entry.label = fp.groupLabel;
          const optVal = (fp.optionValue ?? "").trim();
          if (optVal && !entry.options.includes(optVal)) {
            entry.options.push(optVal);
          }
        }
      }
    }
    // Add group keys to positionKeys so they pass the documentKeys filter.
    for (const groupKey of groupBundles.keys()) {
      positionKeys.add(groupKey);
    }

    const mappingKeys = new Set<string>();
    // Legacy AcroForm mappings are inert once visual placements exist:
    // `fillPdfForm` flattens the form and fills exclusively from
    // `fieldPositions`, so a mapping-only field can never reach the PDF.
    // Surfacing it here only adds a row the user can never make matter.
    if (dbTemplate?.pdfFieldMappings?.length && !hasFieldPositions) {
      for (const m of dbTemplate.pdfFieldMappings) {
        if (m.canonicalKey) mappingKeys.add(m.canonicalKey);
      }
    }

    const documentKeys = new Set([
      ...bodyKeys,
      ...positionKeys,
      ...mappingKeys,
    ]);

    // Build an editable field for `k`. Checkbox placements surface as a
    // real checkbox toggle; other types resolve normally.
    const pushFormField = (k: string, placementType?: string) => {
      pushIfNew(resolveField(k, { fieldType: placementType }));
    };

    // 1. Visual field positions (primary path for modern PDF templates)
    for (const k of positionKeys) {
      // Group key → synthesize a single-select field with the options
      // gathered from member placements.
      const group = groupBundles.get(k);
      if (group) {
        pushIfNew({
          key: k,
          label: group.label,
          fieldType: "select",
          categoryId: "form",
          options: group.options,
        });
        continue;
      }
      const fp = dbTemplate?.fieldPositions?.find((p) => p.canonicalKey === k);
      if (fp?.fieldType === "signature") continue;
      pushFormField(k, fp?.fieldType);
    }

    // 2. Legacy AcroForm pdfFieldMappings
    for (const k of mappingKeys) {
      const m = dbTemplate?.pdfFieldMappings?.find((p) => p.canonicalKey === k);
      if (m?.fieldType === "signature") continue;
      pushFormField(k, m?.fieldType);
    }

    // 3. Letter template extracted placeholders — only include fields
    // that actually appear in this document's body, not every field
    // the template defines.
    if (dbTemplate?.extractedFields?.length) {
      for (const f of dbTemplate.extractedFields) {
        if (f.fieldType === "signature") continue;
        if (!documentKeys.has(f.key)) continue;
        pushIfNew(
          resolveField(f.key, {
            label: f.label,
            description: f.description,
            fieldType: f.fieldType,
          }),
        );
      }
    }

    // 3b. Body-only placeholders not covered by extractedFields (e.g.
    // custom fields inserted after the last template extraction).
    for (const key of bodyKeys) {
      if (key === "signature") continue;
      pushIfNew(resolveField(key));
    }

    // 4. Keys from saved fieldValues — only if the field is actually
    // referenced by this document (body, positions, or mappings).
    const vals = doc.fieldValues ?? {};
    for (const key of Object.keys(vals)) {
      if (key === "signature") continue;
      if (!documentKeys.has(key)) continue;
      pushIfNew(resolveField(key));
    }

    return collected;
  }, [
    doc.fieldValues,
    doc.editedHtml,
    dbTemplate?.templateKey,
    dbTemplate?.templateType,
    dbTemplate?.templateHtml,
    dbTemplate?.extractedContent,
    dbTemplate?.fieldPositions,
    dbTemplate?.pdfFieldMappings,
    dbTemplate?.extractedFields,
    hasFieldPositions,
    customFieldByKey,
    firmContactCustomFieldByKey,
  ]);

  // Deduplicate fields by key (hardcoded forms may reference same canonical key multiple times)
  const uniqueEditableFields = useMemo(() => {
    const seen = new Set<string>();
    return editableFields.filter((f) => {
      if (seen.has(f.key)) return false;
      seen.add(f.key);
      return true;
    });
  }, [editableFields]);

  // Custom category labels keyed by categoryId so non-canonical groups
  // render under their user-defined heading (e.g. "Custom Fields").
  const customCategoryLabels = useMemo(() => {
    const map = new Map<string, string>();
    if (!customFieldDefs) return map;
    for (const def of customFieldDefs) {
      if (!map.has(def.categoryId)) map.set(def.categoryId, def.categoryLabel);
    }
    return map;
  }, [customFieldDefs]);

  // Group fields by category
  const fieldsByCategory = useMemo(() => {
    const categoryOrder = FIELD_CATEGORIES.map((c) => c.id);
    const groups = new Map<
      string,
      { label: string; fields: typeof uniqueEditableFields }
    >();

    for (const f of uniqueEditableFields) {
      const catId = f.categoryId;
      const group =
        groups.get(catId) ??
        (() => {
          const cat = FIELD_CATEGORIES.find((c) => c.id === catId);
          const created = {
            label: cat?.label ?? customCategoryLabels.get(catId) ?? "Other",
            fields: [],
          };
          groups.set(catId, created);
          return created;
        })();
      group.fields.push(f);
    }

    return Array.from(groups.entries())
      .sort(([a], [b]) => {
        const ai = categoryOrder.indexOf(a);
        const bi = categoryOrder.indexOf(b);
        return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
      })
      .map(([id, group]) => ({ id, ...group }));
  }, [uniqueEditableFields, customCategoryLabels]);

  const handleFieldChange = useCallback((key: string, value: string) => {
    setEditedValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleFieldBlur = useCallback(() => {
    setFocusedField(null);
    void fieldsAutosave.flush();
  }, [fieldsAutosave]);

  const handlePresetSelect = useCallback(
    (categoryId: string, presetId: Id<"fieldPresets">) => {
      setPresetSelections((prev) => ({ ...prev, [categoryId]: presetId }));
      const preset = allPresets?.find((p) => p._id === presetId);
      if (preset) {
        const presetValues = preset.values;
        setEditedValues((prev) => {
          const next = { ...prev, ...presetValues };
          editedValuesRef.current = next;
          return next;
        });
        void fieldsAutosave.flush();
      }
    },
    [allPresets, fieldsAutosave],
  );

  const handleEditClose = useCallback(async () => {
    await fieldsAutosave.flush();
    setEditing(false);
    setFocusedField(null);
  }, [fieldsAutosave]);

  useEffect(() => {
    if (!focusedField || !previewRef.current) return;
    requestAnimationFrame(() => {
      const el = previewRef.current?.querySelector<HTMLElement>(
        `[data-field-key="${CSS.escape(focusedField)}"]`,
      );
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [focusedField]);

  const canEditFields = hasPdfMappings || hasFieldPositions || !!bodyHtml;
  const canEditContent = !!bodyHtml && !hasPdfMappings && !hasFieldPositions;
  const hasEditedHtml = !!doc.editedHtml;
  const contactSourceLinkKind = doc.contactSourceLinkKind;

  const handleWysiwygSave = useCallback(
    async (html: string, padding?: PaddingSource) => {
      await updateEditedHtml({
        precedentId: doc._id,
        editedHtml: html,
        ...(padding && {
          headerTopPadding: padding.headerTopPadding ?? undefined,
          headerSidePadding: padding.headerSidePadding ?? undefined,
          headerLeftPadding: padding.headerLeftPadding ?? undefined,
          headerRightPadding: padding.headerRightPadding ?? undefined,
          bodyTopPadding: padding.bodyTopPadding ?? undefined,
          bodySidePadding: padding.bodySidePadding ?? undefined,
          bodyLeftPadding: padding.bodyLeftPadding ?? undefined,
          bodyRightPadding: padding.bodyRightPadding ?? undefined,
          bodyBottomPadding: padding.bodyBottomPadding ?? undefined,
          footerBottomPadding: padding.footerBottomPadding ?? undefined,
          footerSidePadding: padding.footerSidePadding ?? undefined,
          footerLeftPadding: padding.footerLeftPadding ?? undefined,
          footerRightPadding: padding.footerRightPadding ?? undefined,
        }),
      });
    },
    [updateEditedHtml, doc._id],
  );

  const handleResetToTemplate = useCallback(async () => {
    try {
      await updateEditedHtml({
        precedentId: doc._id,
        editedHtml: "",
      });
      toast.success("Reset to original template");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to reset"));
    }
  }, [updateEditedHtml, doc._id]);

  const handleWysiwygClose = useCallback(async () => {
    await wysiwygEditorRef.current?.flush();
    setWysiwygEditing(false);
  }, []);

  const wysiwygInitialHtml = useMemo(() => {
    // Keep variant blocks intact in the editor so the user can switch tabs
    // to edit any option. Variants are resolved only at preview / export.
    //
    // Field placeholders are seeded as atomic chips labelled with the current
    // resolved value (NOT baked in as plain text) so the saved editedHtml
    // keeps its `{{key}}` tokens and later Edit Fields changes still pull
    // through to the rendered document (MOR-704). Re-opening a previously
    // edited document runs the same pass so any tokens it carries show the
    // freshest values as their chip labels.
    // Manual list markers are normalised into fixed-width boxes (MOR-977) so
    // the content editor shows the same alignment as the preview/exports —
    // the TemplateEditor's ListMarker node round-trips them into editedHtml.
    const values = enrichWithSignature(doc.fieldValues ?? {}, signatureData);
    const checkboxKeys = collectCheckboxKeys(dbTemplate);
    if (doc.editedHtml) {
      return normalizeManualListMarkers(
        bakeFieldPlaceholdersToChips(doc.editedHtml, values, {
          checkboxKeys,
          fieldLabels: templateFieldLabels,
        }),
      );
    }
    if (!bodyHtml) return "";
    // Fill never-baked variant options from the snippet library so the
    // editor shows the library content (which the user can then tweak
    // per-document) instead of the `Edit this for "X"` seed placeholder.
    return normalizeManualListMarkers(
      bakeFieldPlaceholdersToChips(
        fillVariantSnippetPlaceholders(
          bodyHtml,
          variantSnippetsForEditor,
          doc.snippetSelections,
        ),
        values,
        {
          checkboxKeys,
          fieldLabels: templateFieldLabels,
          collectionData: doc.collectionData,
        },
      ),
    );
  }, [
    doc.editedHtml,
    bodyHtml,
    doc.fieldValues,
    doc.snippetSelections,
    signatureData,
    variantSnippetsForEditor,
    dbTemplate,
    doc.collectionData,
    templateFieldLabels,
  ]);

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          // Flush any pending field edits before the dialog closes so the
          // user never loses keystrokes on close.
          void fieldsAutosave.flush();
        }
        onOpenChange(o);
      }}
    >
      {/*
       * `h-[90vh]` (not `max-h-[90vh]`) is required so the dialog establishes
       * a definite height for the flex column. With only `max-h`, the
       * `flex-1` children fall back to content-sized layout and the inner
       * editor body's `overflow-auto` never gets a bounded height — wheel
       * scrolling silently dies even though arrow-key navigation still
       * appears to work via ProseMirror's `scrollIntoView` walking up to the
       * nearest scrollable ancestor.
       */}
      <DialogContent
        className={cn(
          "flex h-[90vh] flex-col gap-0 p-0",
          wysiwygEditing ? "max-w-7xl" : editing ? "max-w-7xl" : "max-w-5xl",
        )}
      >
        <VisuallyHidden.Root>
          <DialogTitle>{doc.name} Preview</DialogTitle>
          <DialogDescription>
            Preview, edit, and export this precedent document.
          </DialogDescription>
        </VisuallyHidden.Root>

        {/* Header — extra right padding leaves clearance for the dialog's
         * built-in close (X) button so action buttons don't sit flush against
         * it. */}
        <div className="border-b border-border py-3 pl-6 pr-12">
          {/* Row 1: Document title + action buttons */}
          <div className="flex items-center gap-3">
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <Eye className="size-4 shrink-0 text-primary/40" />
                    <span className="truncate text-sm font-medium text-primary/70">
                      {doc.name}
                    </span>
                    {hasEditedHtml && (
                      <Badge
                        variant="secondary"
                        className="shrink-0 text-[10px]"
                      >
                        Edited
                      </Badge>
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  align="start"
                  className="max-w-md text-xs"
                >
                  {doc.name}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <div className="flex shrink-0 items-center gap-2">
              {canEditFields && !editing && !wysiwygEditing && (
                <button
                  type="button"
                  onClick={() => {
                    setEditedValues({ ...(doc.fieldValues ?? {}) });
                    setEditing(true);
                  }}
                  className="flex items-center gap-1 whitespace-nowrap rounded px-2 py-1 text-xs text-primary/50 hover:bg-secondary"
                >
                  <Pencil className="size-3 shrink-0" />
                  Edit Fields
                </button>
              )}
              {canEditContent && !editing && !wysiwygEditing && (
                <button
                  type="button"
                  onClick={() => setWysiwygEditing(true)}
                  className="flex items-center gap-1 whitespace-nowrap rounded px-2 py-1 text-xs text-primary/50 hover:bg-secondary"
                >
                  <Pencil className="size-3 shrink-0" />
                  Edit Content
                </button>
              )}
              {hasEditedHtml && !wysiwygEditing && !editing && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button
                      type="button"
                      className="flex items-center gap-1 whitespace-nowrap rounded px-2 py-1 text-xs text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/20"
                    >
                      <RotateCcw className="size-3 shrink-0" />
                      Reset to Template
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        Reset to original template?
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        This will discard all saved content edits and restore
                        the document to the original template. This action
                        cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleResetToTemplate}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Reset
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
              {!editing && !wysiwygEditing && !doc.isThirdPartyForm && (
                <LetterheadOverridePicker
                  currentOverride={
                    // Legacy `{ mode, variantIds }` rows have no `kind` and
                    // never matched the picker's checks — treat as unset.
                    doc.letterheadOverride && "kind" in doc.letterheadOverride
                      ? doc.letterheadOverride
                      : undefined
                  }
                  firmDefaultBundleName={
                    brandingProfile?.firmDefaultLetterheadBundleName ?? null
                  }
                  favouriteBundleNames={
                    brandingProfile?.favouriteLetterheadBundleNames ?? []
                  }
                  allBundles={allBundles}
                  allVariants={allVariants}
                  onChange={onSetLetterheadOverride}
                />
              )}
              {!editing && !wysiwygEditing && (
                <DocumentDownloadMenu
                  doc={doc}
                  brandingProfile={brandingProfile}
                  dbTemplate={dbTemplate}
                  asText
                  gallagherValues={isGallagherDoc ? activeValues : undefined}
                  signatureData={signatureData}
                  variants={resolvedVariants}
                  variantSelections={mergeVariantSelections(
                    job?.variantSelections,
                    doc.variantSelections,
                  )}
                  variantSnippets={variantSnippetsForEditor}
                  snippetSelections={doc.snippetSelections}
                  buildHtmlLetterAttachmentParts={
                    buildHtmlLetterAttachmentParts
                  }
                />
              )}
              {editing && (
                <>
                  <AutosaveStatus
                    status={fieldsAutosave.status}
                    lastSavedAt={fieldsAutosave.lastSavedAt}
                    onRetry={fieldsAutosave.retry}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void handleEditClose()}
                    className="h-7 px-2.5 text-xs"
                  >
                    <X className="mr-1 size-3" />
                    Close
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Row 2: Variant pickers — separate line to avoid header cramping */}
          {!editing &&
            !wysiwygEditing &&
            variantGroups &&
            variantGroups.length > 0 &&
            onSetVariantSelection && (
              <div className="mt-2 border-t border-border/40 pt-2">
                <VariantPicker
                  groups={variantGroups}
                  templateHtml={dbTemplate?.templateHtml}
                  jobSelections={job?.variantSelections}
                  docSelections={doc.variantSelections}
                  onChange={(slug, value) =>
                    void onSetVariantSelection(slug, value)
                  }
                  onClear={
                    onClearVariantSelection
                      ? (slug) => void onClearVariantSelection(slug)
                      : undefined
                  }
                  blocks={variantBlocksForPicker}
                  blockSnippetSelections={doc.snippetSelections}
                  blockOverrideSnippets={(b) => {
                    const mergedSel = mergeVariantSelections(
                      job?.variantSelections,
                      doc.variantSelections,
                    );
                    const opt = mergedSel?.[b.groupSlug];
                    return variantSnippetsForEditor
                      .filter(
                        (s) =>
                          s.groupSlug === b.groupSlug &&
                          (!opt || s.optionValue === opt),
                      )
                      .map((s) => ({ _id: s._id, name: s.name }));
                  }}
                  onBlockSnippetChange={
                    onSetSnippetSelection
                      ? (vbId, snippetId) =>
                          void onSetSnippetSelection(vbId, snippetId)
                      : undefined
                  }
                  onBlockSnippetClear={
                    onClearSnippetSelection
                      ? (vbId) => void onClearSnippetSelection(vbId)
                      : undefined
                  }
                  dense
                  hideOrphanWarning
                />
              </div>
            )}
        </div>

        {/* WYSIWYG Editor — full-width content editing.
         *
         * `min-h-0` is required so this flex item can shrink below the
         * editor's intrinsic content height. Without it the inner editor
         * body's `flex-1 overflow-auto` never gets a bounded height and the
         * scrollbar is suppressed (you literally cannot scroll the letter).
         */}
        {wysiwygEditing && (
          <div className="min-h-0 flex-1 overflow-hidden">
            <TemplateEditor
              ref={wysiwygEditorRef}
              initialHtml={wysiwygInitialHtml}
              onAutosave={handleWysiwygSave}
              onClose={handleWysiwygClose}
              templatePadding={doc}
              brandingPadding={brandingProfile}
              presetColors={brandingProfile?.editorPresetColors}
              defaultFontFamily={brandingProfile?.editorFontFamily}
              defaultLineHeight={brandingProfile?.editorLineHeight}
              bodyTextColor={brandingProfile?.bodyTextColor}
              defaultFontSize={brandingProfile?.editorFontSize}
              defaultParagraphSpacingBefore={
                brandingProfile?.editorParagraphSpacingBefore
              }
              defaultParagraphSpacingAfter={
                brandingProfile?.editorParagraphSpacingAfter
              }
              variantGroups={variantGroupsForEditor}
              variantSnippets={variantSnippetsForEditor}
              variantSelections={mergeVariantSelections(
                job?.variantSelections,
                doc.variantSelections,
              )}
              variantEditingMode="document"
            />
          </div>
        )}

        {/* Body: split or full-width (field editing or preview) */}
        {!wysiwygEditing && (
          <div
            className={cn(
              "flex flex-1 overflow-hidden",
              editing && "divide-x divide-border",
            )}
          >
            {/* Edit panel */}
            {editing && (
              <div className="flex w-[30%] min-w-[280px] flex-col">
                <div className="flex-1 overflow-y-auto p-5">
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-primary/80">
                      Edit Fields
                    </h3>
                  </div>
                  {/* Source picker — only renders for docs generated from
                    a job-contact-source fan-out. Lets the practitioner
                    swap which security interest / liability / etc. row
                    this document resolves its source fields from
                    without leaving the editor. Calls
                    `updateJobPrecedentSourceLink` which re-resolves the
                    `contact_job_<kind>_*` field values server-side and
                    re-fills any PDF positions. */}
                  {doc.jobContactId &&
                    contactSourceLinkKind &&
                    docSourceRows &&
                    (() => {
                      const kind = contactSourceLinkKind;
                      const entries = docSourceRows[kind] ?? [];
                      if (entries.length === 0) return null;
                      const currentId = doc.contactSourceLinkId;
                      return (
                        <div className="mb-5 rounded-lg border border-pink-200 bg-pink-50/60 px-3 py-2.5 text-xs dark:border-pink-900/40 dark:bg-pink-950/15">
                          <label className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-pink-700/80 dark:text-pink-300/70">
                            {CONTACT_SOURCE_KIND_LABELS[kind]} source
                          </label>
                          <select
                            value={currentId ?? ""}
                            onChange={(e) => {
                              const next = e.target.value;
                              if (!next || next === currentId) return;
                              void handleSwapSourceLink(kind, next);
                            }}
                            className="h-7 w-full rounded border border-pink-300/60 bg-white px-1.5 text-[12px] text-pink-950 focus:border-pink-400 focus:outline-none dark:border-pink-800/40 dark:bg-pink-950/30 dark:text-pink-100"
                          >
                            {entries.map((entry) => {
                              const { link } = entry;
                              const detail = link.detail ?? "";
                              const amount =
                                link.amount !== undefined &&
                                Number.isFinite(link.amount)
                                  ? new Intl.NumberFormat("en-AU", {
                                      style: "currency",
                                      currency: "AUD",
                                      maximumFractionDigits: 0,
                                    }).format(link.amount)
                                  : "";
                              const tail = [detail, amount]
                                .filter(Boolean)
                                .join(" — ");
                              const label = tail
                                ? `${link.roleLabel} — ${tail}`
                                : link.roleLabel;
                              return (
                                <option
                                  key={link.sourceId}
                                  value={link.sourceId}
                                >
                                  {label}
                                </option>
                              );
                            })}
                          </select>
                          <p className="mt-1.5 text-[10px] text-pink-700/70 dark:text-pink-300/60">
                            Changing the source re-resolves all{" "}
                            {CONTACT_SOURCE_KIND_LABELS[kind].toLowerCase()}{" "}
                            fields below.
                          </p>
                        </div>
                      );
                    })()}
                  <div className="space-y-5">
                    {fieldsByCategory.map((cat) => {
                      const isPresetCategory = DEFAULT_CATEGORIES.has(cat.id);
                      const catPresets = isPresetCategory
                        ? (presetsByCategory.get(cat.id) ?? [])
                        : [];
                      const hasPresets =
                        isPresetCategory && catPresets.length > 0;

                      const sortedPresets: typeof catPresets = hasPresets
                        ? [...catPresets].sort((a, b) => {
                            const aFav = favouriteSet.has(a._id) ? 0 : 1;
                            const bFav = favouriteSet.has(b._id) ? 0 : 1;
                            if (aFav !== bFav) return aFav - bFav;
                            return a.name.localeCompare(b.name);
                          })
                        : [];

                      return (
                        <div key={cat.id}>
                          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-primary/40">
                            {cat.label}
                          </h4>
                          {hasPresets && (
                            <EditPresetSelector
                              presets={sortedPresets}
                              selectedId={presetSelections[cat.id]}
                              favouriteSet={favouriteSet}
                              orgId={orgId}
                              onSelect={(id) => handlePresetSelect(cat.id, id)}
                              toggleFavourite={toggleFavourite}
                            />
                          )}
                          <div
                            className={cn("space-y-3", hasPresets && "mt-3")}
                          >
                            {cat.fields.map((f) => (
                              <EditFieldInput
                                key={f.key}
                                field={f}
                                value={editedValues[f.key] ?? ""}
                                onChange={(v) => handleFieldChange(f.key, v)}
                                onFocus={() => setFocusedField(f.key)}
                                onBlur={handleFieldBlur}
                                isEmpty={!editedValues[f.key]}
                                practitionerOptions={practitionerOptions}
                                presetPractitionerOptions={
                                  presetPractitionerOptions
                                }
                                primaryPractitionerName={
                                  editedValues["practitioner_name"]
                                }
                                fieldSource={classifySource(f.key)}
                              />
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Preview area */}
            <div
              ref={previewRef}
              className={cn("flex-1 overflow-auto", editing && "w-[70%]")}
            >
              {/* Orphaned variant-group warning — rendered here as a full-width
                banner rather than inline in the header, where it broke the
                toolbar layout. */}
              {(() => {
                const orphanSlugs = getOrphanVariantSlugs(
                  dbTemplate?.templateHtml,
                  variantGroups ?? [],
                );
                if (orphanSlugs.length === 0) return null;
                return (
                  <div className="border-b border-amber-200 bg-amber-50 px-6 py-2.5 text-xs text-amber-700 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-400">
                    This template references variant{" "}
                    {orphanSlugs.length === 1 ? "group" : "groups"}{" "}
                    {orphanSlugs.map((s) => `"${s}"`).join(", ")} which no
                    longer exist in this org. Recreate the group to expose
                    options here.
                  </div>
                );
              })()}
              {!editing && (dbTemplate?.attachments?.length ?? 0) > 0 && (
                <div className="border-b border-border/50 bg-background px-6 py-3">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-primary/60">
                    <Paperclip className="size-3.5 text-primary/35" />
                    <span className="font-medium text-primary/70">
                      Attachments:
                    </span>
                    {doc.attachments?.map((attachment) => (
                      <Badge
                        key={`${attachment.status}-${attachment.name}-${attachment.fileName ?? ""}`}
                        variant={
                          attachment.status === "resolved"
                            ? "secondary"
                            : "destructive"
                        }
                        className="text-[10px]"
                      >
                        {attachment.name}
                        {attachment.status === "resolved" &&
                        (attachment.fileName ||
                          attachment.generatedPrecedentName)
                          ? `: ${attachment.fileName ?? attachment.generatedPrecedentName}`
                          : attachment.status === "skipped"
                            ? " skipped"
                            : " missing"}
                      </Badge>
                    ))}
                    <button
                      type="button"
                      onClick={() => setEditingAttachments(true)}
                      className="ml-auto flex items-center gap-1 rounded px-2 py-0.5 text-[11px] text-primary/60 hover:bg-secondary"
                    >
                      <Pencil className="size-3" />
                      Edit attachments
                    </button>
                  </div>
                </div>
              )}
              {/* Filled PDF preview (for PDF form templates) */}
              {hasFilledPdf && (
                <div className="h-full w-full" style={{ minHeight: "80vh" }}>
                  <PdfViewer
                    url={doc.filledPdfUrl ?? ""}
                    title={`${doc.name} — Filled PDF`}
                    /*
                    Overlay clickable hot zones on every checkbox
                    placement so the user can toggle ticks directly on
                    the PDF (AcroForm-style). The mutation re-fills the
                    PDF so the visual state catches up with the click
                    within a second or two — the preview here is just
                    cosmetic feedback.
                  */
                    renderPageOverlay={({
                      pageNumber,
                      cssWidth,
                      pdfWidthPts,
                      pdfHeightPts,
                    }) => {
                      const positions =
                        (dbTemplate?.fieldPositions as
                          | Array<{
                              id?: string;
                              canonicalKey?: string;
                              page: number;
                              x: number;
                              y: number;
                              width: number;
                              height: number;
                              fieldType?: string;
                              checkboxValue?: string;
                              defaultChecked?: boolean;
                              groupKey?: string;
                              optionValue?: string;
                            }>
                          | undefined) ?? [];
                      const cssScale = cssWidth / pdfWidthPts;
                      return positions
                        .filter(
                          (fp) =>
                            fp.page === pageNumber &&
                            fp.fieldType === "checkbox",
                        )
                        .map((fp, idx) => {
                          const left = fp.x * cssScale;
                          const top =
                            (pdfHeightPts - fp.y - fp.height) * cssScale;
                          const width = fp.width * cssScale;
                          const height = fp.height * cssScale;
                          return (
                            <button
                              key={fp.id ?? `${pageNumber}:${idx}`}
                              type="button"
                              className="pointer-events-auto absolute cursor-pointer rounded-sm border border-transparent transition hover:border-purple-500/70 hover:bg-purple-500/10"
                              style={{ left, top, width, height }}
                              title={
                                fp.groupKey
                                  ? `${fp.groupKey} → ${fp.optionValue ?? ""}`
                                  : fp.canonicalKey ||
                                    "Click to toggle checkbox"
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                void togglePdfCheckbox(fp);
                              }}
                            />
                          );
                        });
                    }}
                  />
                </div>
              )}

              {!hasFilledPdf && bodyHtml && (
                // MOR-382: preserveEmptyParagraphs inflates `<p></p>` to
                // `<p>&nbsp;</p>` so manual blank lines from the editor
                // render in the Preview tab (browsers collapse empty
                // paragraph margins, swallowing the user's intended gap).
                <div
                  className={cn(
                    "bg-gray-50 dark:bg-secondary/20",
                    editing ? "p-2" : "p-6",
                  )}
                >
                  <PaginatedLetterPreview
                    pageCardClassName="mx-auto shadow-lg print:shadow-none"
                    pageCardStyle={{
                      fontFamily:
                        (!doc.isThirdPartyForm &&
                          brandingProfile?.editorFontFamily) ||
                        "Arial, sans-serif",
                      fontSize:
                        (!doc.isThirdPartyForm &&
                          brandingProfile?.editorFontSize) ||
                        "11pt",
                      lineHeight:
                        (!doc.isThirdPartyForm &&
                          brandingProfile?.editorLineHeight) ||
                        1.6,
                      color:
                        (!doc.isThirdPartyForm &&
                          brandingProfile?.bodyTextColor) ||
                        "#000",
                    }}
                    isThirdPartyForm={doc.isThirdPartyForm}
                    parts={previewParts}
                    docPadding={docPadding}
                    PageRuleBadgeComponent={PageRuleBadge}
                    renderPageActions={
                      doc.isThirdPartyForm
                        ? undefined
                        : (page) => (
                            <PageMarginsPopover
                              pageIndex={page.pageIndex}
                              inherited={resolvePagePadding(
                                {
                                  ...pagePaddingInputs(previewParts),
                                  documentPageOverrides: (
                                    doc.pageMarginOverrides ?? []
                                  ).filter(
                                    (e: { page: number }) =>
                                      e.page !== page.pageIndex,
                                  ),
                                },
                                page.pageIndex,
                              )}
                              current={
                                (doc.pageMarginOverrides ?? []).find(
                                  (e: { page: number }) =>
                                    e.page === page.pageIndex,
                                )?.padding ?? null
                              }
                              canEdit
                              lockedZones={zoneLocksForPage(
                                getVariantsForParts(previewParts),
                                page.pageIndex,
                              )}
                              onSave={(padding) =>
                                setDocumentPageMarginOverride({
                                  precedentId: doc._id,
                                  page: page.pageIndex,
                                  padding,
                                })
                              }
                            />
                          )
                    }
                    bodyHtml={preserveEmptyParagraphs(
                      editing
                        ? replaceFieldPlaceholders(
                            // Prefer the user's edited content when it still
                            // carries field markers (post-MOR-704 content edits
                            // keep `{{key}}` tokens), so the Edit Fields
                            // preview shows the document they actually have.
                            // Legacy editedHtml with fully baked values falls
                            // back to the template body — substitution against
                            // baked text can't highlight or update anything.
                            resolveVariantsForRender(
                              doc.editedHtml && /\{\{\w+/.test(doc.editedHtml)
                                ? pickHtmlForRender(doc.editedHtml, bodyHtml)
                                : bodyHtml,
                              mergeVariantSelections(
                                job?.variantSelections,
                                doc.variantSelections,
                              ),
                              variantSnippetsForEditor,
                              doc.snippetSelections,
                            ),
                            activeValues,
                            {
                              editing,
                              focusedField,
                              classifySource,
                              checkboxKeys: collectCheckboxKeys(dbTemplate),
                              dateKeys: collectDateKeys(dbTemplate),
                              fieldLabels: templateFieldLabels,
                              collectionData: doc.collectionData,
                            },
                          )
                        : (() => {
                            const src = pickHtmlForRender(
                              doc.editedHtml,
                              bodyHtml,
                            );
                            const resolved = resolveVariantsForRender(
                              src,
                              mergeVariantSelections(
                                job?.variantSelections,
                                doc.variantSelections,
                              ),
                              variantSnippetsForEditor,
                              doc.snippetSelections,
                            );
                            // Substitute current field values on BOTH sources.
                            // editedHtml keeps `{{key}}` tokens for its field
                            // chips (see wysiwygInitialHtml), so Edit Fields
                            // changes pull through after a content edit
                            // (MOR-704). Legacy editedHtml with fully baked
                            // values passes through unchanged.
                            return replaceFieldPlaceholders(
                              resolved,
                              activeValues,
                              {
                                checkboxKeys: collectCheckboxKeys(dbTemplate),
                                dateKeys: collectDateKeys(dbTemplate),
                                collectionData: doc.collectionData,
                              },
                            );
                          })(),
                    )}
                    extraSanitiseAttrs={[
                      "data-field-key",
                      "data-field-source",
                      "data-checkbox-mode",
                    ]}
                  />
                </div>
              )}

              {/* Gallagher field values table — web portal, no PDF fill */}
              {!hasFilledPdf &&
                !bodyHtml &&
                doc.templateKey === "form_gallagher" && (
                  <FieldValuesPreview
                    doc={doc}
                    values={activeValues}
                    fieldConfig={dbTemplate?.fieldConfig}
                  />
                )}

              {/* MOR-2342 — the fill refused to produce an overlapping or
                  truncated PDF; tell the practitioner what to fix. */}
              {doc.pdfFillError && (
                <div className="mx-6 mt-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-200">
                  <p className="font-medium">This form could not be filled</p>
                  <p className="mt-1">{doc.pdfFillError}</p>
                </div>
              )}

              {/* PDF fallback — for reference documents or PDF forms still filling */}
              {!hasFilledPdf &&
                !bodyHtml &&
                doc.templateKey !== "form_gallagher" &&
                dbTemplate?.sourceDocumentUrl && (
                  <div className="h-full w-full" style={{ minHeight: "80vh" }}>
                    <PdfViewer
                      url={dbTemplate.sourceDocumentUrl}
                      title={doc.name}
                    />
                  </div>
                )}

              {/* Truly empty — no filled PDF, no HTML, no source PDF */}
              {!hasFilledPdf &&
                !bodyHtml &&
                doc.templateKey !== "form_gallagher" &&
                !dbTemplate?.sourceDocumentUrl && (
                  <div className="flex items-center justify-center py-20">
                    <p className="text-sm text-primary/40">
                      Preview not available — template not yet uploaded
                    </p>
                  </div>
                )}
            </div>
          </div>
        )}
      </DialogContent>
      <EditAttachmentsDialog
        precedent={{
          _id: doc._id,
          jobId: doc.jobId,
          orgId,
          templateKey: doc.templateKey,
          name: doc.name,
          attachments: doc.attachments,
        }}
        dbTemplate={dbTemplate}
        siblingPrecedents={siblingPrecedents}
        open={editingAttachments}
        onOpenChange={setEditingAttachments}
      />
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Edit Field Input — simplified input for the edit panel
// ---------------------------------------------------------------------------

const PRACTITIONER_SELECT_FIELDS = new Set([
  "second_practitioner_name",
  "signatory_name",
]);

const FIELD_SOURCE_BADGE: Record<
  FieldSource,
  { label: string; className: string }
> = {
  job: {
    label: "Job",
    className:
      "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950/30 dark:text-emerald-400 dark:border-emerald-800",
  },
  org: {
    label: "Org",
    className:
      "bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-950/30 dark:text-blue-400 dark:border-blue-800",
  },
  custom: {
    label: "Custom",
    className:
      "bg-violet-100 text-violet-800 border-violet-300 dark:bg-violet-950/30 dark:text-violet-400 dark:border-violet-800",
  },
  template: {
    label: "Unmapped",
    className:
      "bg-cyan-100 text-cyan-800 border-cyan-300 dark:bg-cyan-950/30 dark:text-cyan-400 dark:border-cyan-800",
  },
  manual: {
    label: "Manual",
    className:
      "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-800",
  },
  job_contact: {
    label: "Job Contact",
    className:
      "bg-rose-100 text-rose-800 border-rose-300 dark:bg-rose-950/30 dark:text-rose-400 dark:border-rose-800",
  },
  firm_contact: {
    label: "Firm Contact",
    className:
      "bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-950/30 dark:text-orange-400 dark:border-orange-800",
  },
  job_contact_source: {
    label: "Job Contact Data",
    className:
      "bg-pink-100 text-pink-800 border-pink-300 dark:bg-pink-950/30 dark:text-pink-400 dark:border-pink-800",
  },
  firm_reference: {
    label: "Firm Ref",
    className:
      "bg-teal-100 text-teal-800 border-teal-300 dark:bg-teal-950/30 dark:text-teal-400 dark:border-teal-800",
  },
};

function EditFieldInput({
  field,
  value,
  onChange,
  onFocus,
  onBlur,
  isEmpty,
  practitionerOptions,
  presetPractitionerOptions,
  primaryPractitionerName,
  fieldSource,
}: {
  field: {
    key: string;
    label: string;
    description?: string;
    fieldType: string;
    options?: string[];
  };
  value: string;
  onChange: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  isEmpty?: boolean;
  practitionerOptions?: string[];
  /**
   * Registered practitioners from the firm's Practitioner Details presets.
   * Only these can be a joint appointee, so `second_practitioner_name` uses
   * this list rather than the job team.
   */
  presetPractitionerOptions?: string[];
  primaryPractitionerName?: string;
  fieldSource?: FieldSource;
}) {
  const sourceBadge = fieldSource ? FIELD_SOURCE_BADGE[fieldSource] : null;
  // Never render a bare, unlabelled control — fall back to a humanised key.
  const displayLabel =
    field.label?.trim() ||
    field.key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  // Hook must run unconditionally, so it sits above the select/checkbox
  // early returns even though only the text path uses it.
  const filteredPractitionerOptions = useMemo(() => {
    if (field.key === "second_practitioner_name") {
      const candidates = presetPractitionerOptions ?? [];
      return primaryPractitionerName
        ? candidates.filter((n) => n !== primaryPractitionerName)
        : candidates;
    }
    return practitionerOptions ?? [];
  }, [
    practitionerOptions,
    presetPractitionerOptions,
    field.key,
    primaryPractitionerName,
  ]);

  if (
    field.fieldType === "select" &&
    field.options &&
    field.options.length > 0
  ) {
    // Single-select question — checkbox group bundle. Click an option to
    // set the group value; click again to clear.
    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1.5">
          <label className="text-xs font-medium text-primary/60">
            {displayLabel}
          </label>
          {sourceBadge && (
            <span
              className={cn(
                "rounded border px-1 py-0.5 text-[9px] font-medium leading-none",
                sourceBadge.className,
              )}
            >
              {sourceBadge.label}
            </span>
          )}
          {isEmpty && (
            <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[9px] font-medium leading-none text-amber-600 dark:bg-amber-950/30">
              Missing
            </span>
          )}
        </div>
        {field.description && (
          <p className="text-[11px] text-primary/35">{field.description}</p>
        )}
        <div className="flex flex-wrap gap-1">
          {field.options.map((opt) => {
            const active = value === opt;
            return (
              <button
                key={opt}
                type="button"
                className={cn(
                  "h-8 rounded-md border px-2.5 text-xs transition",
                  active
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-primary/15 bg-background text-primary/70 hover:border-primary/30",
                )}
                onFocus={onFocus}
                onBlur={onBlur}
                onClick={() => onChange(active ? "" : opt)}
              >
                {opt}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (field.fieldType === "checkbox") {
    const checked = value === "true" || value === "yes" || value === "1";
    return (
      <div className="flex items-center gap-2 py-1">
        <input
          type="checkbox"
          id={field.key}
          checked={checked}
          onChange={(e) => onChange(e.target.checked ? "true" : "false")}
          onFocus={onFocus}
          onBlur={onBlur}
          className="size-4 rounded border-primary/20 text-primary accent-primary"
        />
        <div className="flex items-center gap-1.5">
          <label
            htmlFor={field.key}
            className="text-xs font-medium text-primary/60"
          >
            {displayLabel}
          </label>
          {sourceBadge && (
            <span
              className={cn(
                "rounded border px-1 py-0.5 text-[9px] font-medium leading-none",
                sourceBadge.className,
              )}
            >
              {sourceBadge.label}
            </span>
          )}
        </div>
      </div>
    );
  }

  const showPractitionerCombo =
    PRACTITIONER_SELECT_FIELDS.has(field.key) &&
    filteredPractitionerOptions.length > 0;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1.5">
        <label className="text-xs font-medium text-primary/60">
          {displayLabel}
        </label>
        {sourceBadge && (
          <span
            className={cn(
              "rounded border px-1 py-0.5 text-[9px] font-medium leading-none",
              sourceBadge.className,
            )}
          >
            {sourceBadge.label}
          </span>
        )}
        {isEmpty && (
          <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[9px] font-medium leading-none text-amber-600 dark:bg-amber-950/30">
            Missing
          </span>
        )}
      </div>
      {field.description && (
        <p className="text-[11px] text-primary/35">{field.description}</p>
      )}
      {showPractitionerCombo ? (
        <EditPractitionerComboInput
          value={value}
          options={filteredPractitionerOptions}
          onChange={onChange}
          isEmpty={!!isEmpty}
        />
      ) : (
        <Input
          type={
            field.fieldType === "email"
              ? "email"
              : field.fieldType === "number"
                ? "number"
                : "text"
          }
          placeholder={field.fieldType === "date" ? "YYYY-MM-DD" : ""}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={onFocus}
          onBlur={onBlur}
          className={cn(
            "h-8 text-sm",
            isEmpty && "border-amber-200 dark:border-amber-900/30",
          )}
        />
      )}
    </div>
  );
}

/**
 * Practitioner picker for the day-one edit panel.
 * Matches the PresetSelector visual pattern.
 */
function EditPractitionerComboInput({
  value,
  options,
  onChange,
  isEmpty,
}: {
  value: string;
  options: string[];
  onChange: (value: string) => void;
  isEmpty: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [customMode, setCustomMode] = useState(false);
  const customInputRef = useRef<HTMLInputElement>(null);

  const handleSelect = (name: string) => {
    onChange(name);
    setCustomMode(false);
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setCustomMode(false);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex h-8 w-full items-center justify-between rounded-lg border px-3 text-sm transition hover:border-border",
            isEmpty
              ? "border-amber-200 dark:border-amber-900/30"
              : "border-border/50",
            "bg-background",
          )}
        >
          <span className={value ? "text-primary" : "text-primary/40"}>
            {value || "Select practitioner..."}
          </span>
          <ChevronDown
            className={cn(
              "size-3.5 text-primary/30 transition",
              open && "rotate-180",
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <button
          type="button"
          className={cn(
            "flex w-full cursor-pointer items-center px-3 py-2 text-sm transition",
            !value
              ? "bg-primary/5 text-primary"
              : "text-primary/40 hover:bg-secondary/50",
          )}
          onClick={() => handleSelect("")}
        >
          None
        </button>
        {options.map((name) => (
          <button
            key={name}
            type="button"
            className={cn(
              "flex w-full cursor-pointer items-center px-3 py-2 text-sm transition",
              value === name
                ? "bg-primary/5 text-primary"
                : "text-primary/70 hover:bg-secondary/50",
            )}
            onClick={() => handleSelect(name)}
          >
            {name}
          </button>
        ))}
        <div className="border-t border-border/50 px-3 py-2">
          {customMode ? (
            <Input
              ref={customInputRef}
              type="text"
              placeholder="Type a custom name..."
              autoFocus
              className="h-8 text-sm"
              defaultValue={!options.includes(value) ? value : ""}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSelect((e.target as HTMLInputElement).value);
                }
              }}
            />
          ) : (
            <button
              type="button"
              className="text-xs text-primary/40 transition hover:text-primary/60"
              onClick={() => setCustomMode(true)}
            >
              Enter custom name...
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Edit Preset Selector — dropdown for selecting a field preset
// ---------------------------------------------------------------------------

function EditPresetSelector({
  presets,
  selectedId,
  favouriteSet,
  orgId,
  onSelect,
  toggleFavourite,
}: {
  presets: Array<Doc<"fieldPresets">>;
  selectedId?: Id<"fieldPresets">;
  favouriteSet: Set<Id<"fieldPresets">>;
  orgId: Id<"organizations">;
  onSelect: (id: Id<"fieldPresets">) => void;
  toggleFavourite: (args: {
    orgId: Id<"organizations">;
    presetId: Id<"fieldPresets">;
  }) => Promise<unknown>;
}) {
  const [open, setOpen] = useState(false);
  const selected = presets.find((p) => p._id === selectedId);

  if (presets.length === 1) {
    return (
      <div className="flex h-9 items-center rounded-lg border border-border/50 bg-secondary/30 px-3 text-sm text-primary/70">
        {presets[0].name}
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex h-9 w-full items-center justify-between rounded-lg border border-border/50 bg-background px-3 text-sm transition hover:border-border"
        >
          <span className={selected ? "text-primary" : "text-primary/40"}>
            {selected?.name ?? "Select preset..."}
          </span>
          <ChevronDown
            className={cn(
              "size-3.5 text-primary/30 transition",
              open && "rotate-180",
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] p-0"
      >
        {presets.map((preset) => {
          const isFav = favouriteSet.has(preset._id);
          const isSelected = preset._id === selectedId;
          return (
            <div
              key={preset._id}
              className={cn(
                "flex cursor-pointer items-center gap-2 px-3 py-2 text-sm transition",
                isSelected
                  ? "bg-primary/5 text-primary"
                  : "text-primary/70 hover:bg-secondary/50",
              )}
            >
              <button
                type="button"
                className="flex flex-1 items-center gap-2 text-left"
                onClick={() => {
                  onSelect(preset._id);
                  setOpen(false);
                }}
              >
                <span className="flex-1">{preset.name}</span>
                {preset.isDefault && (
                  <span className="text-[10px] text-primary/30">Default</span>
                )}
              </button>
              <button
                type="button"
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    await toggleFavourite({ orgId, presetId: preset._id });
                  } catch {
                    /* best-effort toggle */
                  }
                }}
                className="rounded p-0.5 hover:bg-secondary"
              >
                <Star
                  className={cn(
                    "size-3",
                    isFav ? "fill-amber-400 text-amber-400" : "text-primary/20",
                  )}
                />
              </button>
            </div>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Field Values Preview — renders field values as a copyable table
// Used for hardcoded web forms (Gallagher) that don't have templateHtml
// ---------------------------------------------------------------------------

const REQUIRED_GALLAGHER_FIELD_KEYS = [
  "appointment_date",
  "firm_name",
  "practitioner_name",
  "contact_name",
  "contact_email",
  "company_name",
  "acn",
  "business_industry",
  "principal_risk_address",
] as const;

function FieldValuesPreview({
  doc,
  values: valuesProp,
  fieldConfig,
}: {
  doc: DayOneDoc;
  values?: Record<string, string>;
  fieldConfig?: GallagherFieldConfig[];
}) {
  const values: Record<string, string> = useMemo(
    () => valuesProp ?? doc.fieldValues ?? {},
    [valuesProp, doc.fieldValues],
  );
  const [copied, setCopied] = useState(false);

  const fields = useMemo(() => {
    const hiddenFields = new Set(
      (fieldConfig ?? [])
        .filter((entry) => entry.hidden)
        .map((entry) => entry.fieldKey),
    );
    return GALLAGHER_INSURANCE_FIELDS.filter(
      (field) => !hiddenFields.has(field.key),
    ).map((field) => ({
      key: field.key,
      label: field.label,
      value:
        values[field.key] ??
        (field.canonicalKey ? values[field.canonicalKey] : "") ??
        "",
      fieldType:
        CANONICAL_FIELDS_MAP[field.canonicalKey ?? field.key]?.fieldType ??
        field.fieldType,
    }));
  }, [fieldConfig, values]);

  const handleCopyAll = () => {
    const lines = fields.map((f) => `${f.label}\t${f.value}`);
    navigator.clipboard.writeText(lines.join("\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("All fields copied — paste into the Gallagher portal");
  };

  /**
   * Opens Gallagher's portal in a new tab with all fields pre-filled.
   *
   * AJG (insolvencyappointments.ajg.com.au) sits behind Imperva, which has
   * two empirically-confirmed rules we have to work around for a cross-origin
   * POST from morgan.so:
   *
   * 1. Multipart bodies are stripped — submit as `application/x-www-form-urlencoded`.
   * 2. Bodies containing more than one email-shaped value are stripped —
   *    `PrimaryContactEmail` alone is fine, but adding `Nominee*.Email` with a
   *    real `<local>@<domain>` value triggers it. Non-email-shaped strings pass.
   *    We replace `@` with `[at]` in nominee emails to bypass the regex; the
   *    analyst swaps `[at]` → `@` in the portal before submitting (the toast
   *    below prompts them when nominee emails are present). URL-encoding `@`
   *    to `%40` was tried instead but the native form encoder double-encodes
   *    the `%`, landing the value as `josh%40morgan.so` which AJG's email
   *    validator rejects.
   *
   * Flow: open AJG with a GET first (lets Imperva drop its session cookies),
   * then after a short delay POST the prefilled form into the same named
   * window so the analyst lands on a filled entry form they can review.
   */
  const handleOpenGallagherPrefilled = () => {
    const formData = buildGallagherPortalPayload(values);
    if (formData["Nominee1.Email"]) {
      formData["Nominee1.Email"] = formData["Nominee1.Email"].replace(
        "@",
        "[at]",
      );
    }
    if (formData["Nominee2.Email"]) {
      formData["Nominee2.Email"] = formData["Nominee2.Email"].replace(
        "@",
        "[at]",
      );
    }
    const filledFields = Object.entries(formData).filter(([, val]) => val);
    const missingRequiredFields = REQUIRED_GALLAGHER_FIELD_KEYS.filter(
      (key) => !values[key],
    );
    if (missingRequiredFields.length > 0 || !formData.OccupationOfAppointment) {
      console.warn("[Gallagher] Portal payload has missing fields", {
        missingRequiredFields,
        occupationOfAppointmentMissing: !formData.OccupationOfAppointment,
      });
    }

    const gallagherUrl =
      "https://insolvencyappointments.ajg.com.au/Appointment/Insolvency";
    const windowName = "gallagher_portal";

    // Step 1: Open Gallagher page via GET to establish CDN/Imperva cookies
    const w = window.open(gallagherUrl, windowName);
    if (!w) {
      toast.error("Popup blocked — please allow popups for this site");
      console.error("[Gallagher] window.open returned null (popup blocked)");
      return;
    }

    // Step 2: After delay, POST the form data to the same named window
    const delayMs = 3000;
    toast.info("Opening Gallagher portal... data will be filled in a moment");

    setTimeout(() => {
      const form = document.createElement("form");
      form.method = "POST";
      // urlencoded, not multipart — see Imperva-rule note in the function docblock.
      form.enctype = "application/x-www-form-urlencoded";
      form.action = gallagherUrl;
      form.target = windowName;
      form.style.display = "none";

      for (const [name, value] of filledFields) {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = name;
        input.value = value;
        form.appendChild(input);
      }

      document.body.appendChild(form);
      form.submit();

      setTimeout(() => {
        if (form.parentNode) {
          document.body.removeChild(form);
        }
      }, 2000);

      const hasNomineeEmail =
        Boolean(formData["Nominee1.Email"]) ||
        Boolean(formData["Nominee2.Email"]);
      toast.success(
        hasNomineeEmail
          ? "Gallagher form filled — replace [at] with @ in nominee email(s), then click Continue to Preview"
          : "Gallagher form filled — review it and click Continue to Preview",
        { duration: 8000 },
      );
    }, delayMs);
  };

  const isGallagher = doc.templateKey === "form_gallagher";

  return (
    <div className="p-6">
      <div className="mx-auto max-w-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h4 className="text-sm font-semibold text-primary/70">{doc.name}</h4>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCopyAll}
              className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-primary/60 transition hover:bg-muted/50"
            >
              {copied ? (
                <Check className="size-3" />
              ) : (
                <Copy className="size-3" />
              )}
              {copied ? "Copied" : "Copy All"}
            </button>
            {isGallagher && (
              <button
                type="button"
                onClick={handleOpenGallagherPrefilled}
                className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground transition hover:bg-primary/90"
              >
                <ExternalLink className="size-3" />
                Open in Gallagher Portal
              </button>
            )}
          </div>
        </div>
        <div className="overflow-hidden rounded-lg border border-border">
          <table className="w-full">
            <tbody>
              {fields.map((f, i) => (
                <tr
                  key={f.key}
                  className={cn(
                    "border-b border-border last:border-0",
                    i % 2 === 0 ? "bg-muted/20" : "",
                  )}
                >
                  <td className="w-[40%] px-4 py-2.5 text-xs font-medium text-primary/60">
                    {f.label}
                  </td>
                  <td className="select-all px-4 py-2.5 text-sm text-primary/80">
                    {f.value || (
                      <span className="italic text-primary/25">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page Rule Badge — shows which pages the header/footer applies to
// ---------------------------------------------------------------------------

const PAGE_RULE_SHORT_LABELS: Record<PageRule, string> = {
  all: "All pages",
  firstOnly: "First page only",
  notFirst: "All except first",
  oddOnly: "Odd pages",
  evenOnly: "Even pages",
  none: "None",
};

function PageRuleBadge({ rule }: { rule: PageRule }) {
  return (
    <span className="absolute right-2 top-1 rounded bg-primary/5 px-1.5 py-0.5 text-[9px] font-medium text-primary/40">
      {PAGE_RULE_SHORT_LABELS[rule]}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Letterhead Override Picker — grouped dropdown over letterheads + variants
//
// One dropdown, grouped into sections:
//   1. "Use firm default" row when an override is active AND a firm default
//      exists (lets the user clear back to firm default).
//   2. ★ Favourites — anything starred (letterheads OR variants).
//   3. Letterheads — every bundle.
//   4. Variants — every named variant recipe.
//   5. "No letterhead" — variant-free render.
//
// The trigger button label reflects what's currently active so the user
// always sees the source of truth.
// ---------------------------------------------------------------------------

// Local alias of the shared picker — keeps existing call sites in this
// file working unchanged. Implementation lives in
// @/components/branding/LetterheadOverridePicker.
import { LetterheadOverridePicker as SharedLetterheadOverridePicker } from "@/components/branding/LetterheadOverridePicker";
import { VariantPicker } from "@/components/precedents/VariantPicker";
import {
  getOrphanVariantSlugs,
  extractVariantGroupSlugs,
} from "@/components/precedents/VariantPicker.helpers";
const LetterheadOverridePicker = SharedLetterheadOverridePicker;

// ---------------------------------------------------------------------------
// Document Download Menu — Word, PDF options
// ---------------------------------------------------------------------------

/** Get separate letter parts for Word export (proper header/footer zones). */
function getDocumentParts(
  doc: DayOneDoc,
  brandingProfile: BrandingProfile | null | undefined,
  dbTemplate: TemplateDoc | undefined,
  signatureData?: SignatureData,
  variants?: VariantFragments[],
  variantSelections?: Record<string, string>,
  variantSnippets?: VariantSnippetForFill[],
  snippetSelections?: Record<string, string>,
  gallagherValues?: Record<string, string>,
): LetterParts {
  const rawBodyHtml = dbTemplate?.templateHtml;
  const bodyHtml = rawBodyHtml
    ? resolveVariantsForRender(
        rawBodyHtml,
        variantSelections,
        variantSnippets,
        snippetSelections,
      )
    : rawBodyHtml;
  const values = enrichWithSignature(doc.fieldValues ?? {}, signatureData);

  const effectiveHtml = rawBodyHtml
    ? pickHtmlForRender(doc.editedHtml, rawBodyHtml)
    : doc.editedHtml;
  const editedHtml =
    effectiveHtml && effectiveHtml === doc.editedHtml
      ? resolveVariantsForRender(
          effectiveHtml,
          variantSelections,
          variantSnippets,
          snippetSelections,
        )
      : undefined;

  // MOR-382: Inject `&nbsp;` into empty paragraphs so manual blank lines
  // (Enter key in the editor) survive PDF / Word export.
  // Both sources get current field values substituted — editedHtml keeps
  // `{{key}}` tokens for its field chips, so Edit Fields changes made after
  // a content edit still reach the export (MOR-704).
  // MOR-2342 — the Gallagher notification is a web-portal form with no
  // template HTML, so the export used to produce an empty document. Render
  // the same field table the preview shows, from the resolved values the
  // preview passed in (or the saved values when called from the grid).
  const bodyContent =
    doc.templateKey === "form_gallagher"
      ? buildGallagherExportHtml(gallagherValues ?? doc.fieldValues ?? {}, {
          title: doc.name,
          fieldConfig: dbTemplate?.fieldConfig,
        })
      : preserveEmptyParagraphs(
          editedHtml
            ? replaceFieldPlaceholders(editedHtml, values, {
                checkboxKeys: collectCheckboxKeys(dbTemplate),
                dateKeys: collectDateKeys(dbTemplate),
              })
            : bodyHtml
              ? replaceFieldPlaceholders(bodyHtml, values, {
                  checkboxKeys: collectCheckboxKeys(dbTemplate),
                  dateKeys: collectDateKeys(dbTemplate),
                  collectionData: doc.collectionData,
                })
              : "",
        );

  return {
    bodyHtml: bodyContent,
    // Variant-aware path: ordered VariantFragments[] supersede the flat
    // headerImageUrl/etc. fields. Third-party forms get [] which suppresses
    // letterhead entirely; the legacy path's `isThirdPartyForm` guards
    // stay in place for non-variant callers.
    variants: doc.isThirdPartyForm ? [] : (variants ?? []),
    brandingPadding: !doc.isThirdPartyForm ? brandingProfile : undefined,
    templatePadding: !doc.isThirdPartyForm ? dbTemplate : undefined,
    documentPadding: !doc.isThirdPartyForm ? doc : undefined,
    templatePageOverrides: !doc.isThirdPartyForm
      ? (dbTemplate?.pageMarginOverrides ?? null)
      : null,
    documentPageOverrides: !doc.isThirdPartyForm
      ? (doc.pageMarginOverrides ?? null)
      : null,
    defaultFontFamily: !doc.isThirdPartyForm
      ? (brandingProfile?.editorFontFamily ?? undefined)
      : undefined,
    defaultLineHeight: !doc.isThirdPartyForm
      ? (brandingProfile?.editorLineHeight ?? undefined)
      : undefined,
    bodyTextColor: !doc.isThirdPartyForm
      ? (brandingProfile?.bodyTextColor ?? undefined)
      : undefined,
    defaultFontSize: !doc.isThirdPartyForm
      ? (brandingProfile?.editorFontSize ?? undefined)
      : undefined,
    defaultParagraphSpacingBefore: !doc.isThirdPartyForm
      ? (brandingProfile?.editorParagraphSpacingBefore ?? undefined)
      : undefined,
    defaultParagraphSpacingAfter: !doc.isThirdPartyForm
      ? (brandingProfile?.editorParagraphSpacingAfter ?? undefined)
      : undefined,
    pageNumbering: !doc.isThirdPartyForm
      ? resolvePageNumbering(brandingProfile, dbTemplate)
      : null,
  };
}

/** Get combined HTML for browser preview / PDF. */
function getDocumentHtml(
  doc: DayOneDoc,
  brandingProfile: BrandingProfile | null | undefined,
  dbTemplate: TemplateDoc | undefined,
  signatureData?: SignatureData,
  variants?: VariantFragments[],
  variantSelections?: Record<string, string>,
  variantSnippets?: VariantSnippetForFill[],
  snippetSelections?: Record<string, string>,
): string {
  const parts = getDocumentParts(
    doc,
    brandingProfile,
    dbTemplate,
    signatureData,
    variants,
    variantSelections,
    variantSnippets,
    snippetSelections,
  );
  if (parts.bodyHtml) return buildLetterHtml(parts);

  return `<div style="padding:40px;text-align:center;color:#999">Preview not available for this document type.</div>`;
}

/**
 * Merge a job-level variantSelections record with an optional per-document
 * override. Document keys win — anything not present falls back to the
 * job-wide selection. This is the resolution function the renderer uses
 * everywhere variant blocks need to be resolved at preview / export time.
 */
function mergeVariantSelections(
  jobLevel: Record<string, string> | undefined,
  docLevel: Record<string, string> | undefined,
): Record<string, string> | undefined {
  if (!jobLevel && !docLevel) return undefined;
  return { ...(jobLevel ?? {}), ...(docLevel ?? {}) };
}

/**
 * Resolve variant blocks for render / export. Fills any never-baked variant
 * option from the snippet library first (so the selected variant shows its
 * library content instead of the raw `Edit this for "X"` placeholder), then
 * strips the block down to the chosen option.
 */
function resolveVariantsForRender(
  html: string,
  selections: Record<string, string> | undefined,
  snippets: VariantSnippetForFill[] | undefined,
  snippetSelections?: Record<string, string>,
): string {
  const filled =
    snippets && snippets.length > 0
      ? fillVariantSnippetPlaceholders(
          html,
          snippets,
          snippetSelections,
          selections,
        )
      : html;
  // Strip any highlighter background (e.g. green merge-field highlights pasted
  // in from Word / Google Docs and baked into snippet content) so it never
  // reaches the rendered letter or the PDF / Word export. Runs on every
  // render/export path because they all funnel the body through here.
  return stripInlineHighlights(resolveVariantBlocks(filled, selections));
}

/**
 * Pick the best HTML source for preview/export. When the user has edited the
 * document (`editedHtml`), use that — UNLESS it's missing variant blocks that
 * the original template carries. That happens when the document was edited
 * before a variant group was added to the template, or when variant blocks
 * were resolved away in a previous save. In those cases fall back to the
 * template body so variant selections can still take effect.
 */
function pickHtmlForRender(
  editedHtml: string | undefined | null,
  templateBodyHtml: string,
): string {
  if (!editedHtml) return templateBodyHtml;
  const templateSlugs = extractVariantGroupSlugs(templateBodyHtml);
  if (templateSlugs.size === 0) return editedHtml;
  const editedSlugs = extractVariantGroupSlugs(editedHtml);
  for (const slug of templateSlugs) {
    if (!editedSlugs.has(slug)) return templateBodyHtml;
  }
  return editedHtml;
}

function sanitizeFilename(name: string): string {
  return name
    .replace(/[^a-zA-Z0-9\s\-_]/g, "")
    .replace(/\s+/g, "-")
    .toLowerCase();
}

type DayOneAttachment = {
  name: string;
  status: "resolved" | "missing" | "skipped";
  fileName?: string;
  fileType?: string;
  downloadUrl?: string | null;
  generatedPrecedentName?: string | null;
  generatedFilledPdfUrl?: string | null;
};

function getResolvedAttachments(doc: {
  attachments?: DayOneAttachment[];
}): Array<{
  fileName: string;
  fileType?: string;
  downloadUrl: string;
}> {
  return (doc.attachments ?? [])
    .filter(
      (attachment) =>
        attachment.status === "resolved" &&
        (!!attachment.downloadUrl || !!attachment.generatedFilledPdfUrl),
    )
    .map((attachment) => {
      // Generated-precedent attachments resolve to a filled PDF but their
      // `fileName` is set to the template's display name (e.g. "Notice of
      // Appointment") with no extension. Uploaded attachments keep their
      // original filename (which already has `.pdf`). Force `.pdf` on
      // generated ones so Word/Finder can open the file out of the zip.
      const isGeneratedPdf =
        !attachment.downloadUrl && !!attachment.generatedFilledPdfUrl;
      const baseName =
        attachment.fileName ??
        sanitizeFilename(attachment.generatedPrecedentName ?? attachment.name);
      const fileName =
        isGeneratedPdf && !baseName.toLowerCase().endsWith(".pdf")
          ? `${baseName}.pdf`
          : baseName;
      return {
        fileName,
        fileType: isGeneratedPdf
          ? (attachment.fileType ?? "application/pdf")
          : attachment.fileType,
        downloadUrl:
          attachment.downloadUrl ?? attachment.generatedFilledPdfUrl ?? "",
      };
    });
}

/**
 * Per-run caches shared across every document of one bulk download.
 *
 * Day-one annexures are heavily shared — the same Notice of Appointment or
 * ROCAP is attached to most letters in a job — so without these caches a
 * bulk download re-fetched every uploaded annexure and re-rendered every
 * generated HTML annexure once per pack, which is the bulk of why large
 * day-one downloads were so slow (MOR-971).
 */
interface BulkDownloadCaches {
  /** Uploaded / pre-filled attachment fetches, keyed by download URL. */
  attachmentBlobs: Map<string, Promise<Blob>>;
  /** Client-rendered HTML-annexure PDFs, keyed by sibling precedent id. */
  renderedLetterPdfs: Map<string, Promise<Blob>>;
}

function createBulkDownloadCaches(): BulkDownloadCaches {
  return { attachmentBlobs: new Map(), renderedLetterPdfs: new Map() };
}

/**
 * Run `fn` over `items` with at most `limit` in flight, preserving result
 * order. Bulk downloads use this so one document's network waits (fonts,
 * letterhead images, annexure fetches) overlap another's rasterisation
 * instead of strictly serialising every document (MOR-971).
 */
async function mapWithConcurrency<T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const results = new Array<R>(items.length);
  let nextIndex = 0;
  const workers = Array.from(
    { length: Math.max(1, Math.min(limit, items.length)) },
    async () => {
      while (nextIndex < items.length) {
        const index = nextIndex++;
        results[index] = await fn(items[index], index);
      }
    },
  );
  await Promise.all(workers);
  return results;
}

async function fetchAttachmentBlobs(
  doc: { attachments?: DayOneAttachment[] },
  blobCache?: Map<string, Promise<Blob>>,
): Promise<
  Array<{
    fileName: string;
    file: Blob;
    fileType?: string;
  }>
> {
  const attachments = getResolvedAttachments(doc);

  // Fetch every attachment in parallel — packs commonly carry several
  // annexures and the previous one-at-a-time loop serialised the round
  // trips. The optional cache dedupes shared annexures across the
  // documents of a bulk download (MOR-971).
  return Promise.all(
    attachments.map(async (attachment) => {
      const filePromise =
        blobCache?.get(attachment.downloadUrl) ??
        (() => {
          const created = fetch(attachment.downloadUrl).then((response) => {
            if (!response.ok) {
              throw new Error(`Failed to fetch ${attachment.fileName}`);
            }
            return response.blob();
          });
          blobCache?.set(attachment.downloadUrl, created);
          return created;
        })();
      return {
        fileName: attachment.fileName,
        fileType: attachment.fileType,
        file: await filePromise,
      };
    }),
  );
}

function DocumentDownloadMenu({
  doc,
  brandingProfile,
  dbTemplate,
  asText,
  signatureData,
  variants,
  variantSelections,
  variantSnippets,
  snippetSelections,
  buildHtmlLetterAttachmentParts,
  gallagherValues,
}: {
  doc: DayOneDoc;
  brandingProfile: BrandingProfile | null | undefined;
  dbTemplate: TemplateDoc | undefined;
  asText?: boolean;
  /**
   * Resolved Gallagher field values as the preview shows them (job sources +
   * saved overrides). Without them the export falls back to the saved values.
   */
  gallagherValues?: Record<string, string>;
  signatureData?: SignatureData;
  /** Pre-resolved letterhead variants for this doc (ordered, highest priority first). */
  variants?: VariantFragments[];
  /** Job-level variant option selections (sentence-level template variants). */
  variantSelections?: Record<string, string>;
  /** Snippet library — fills never-baked variant options before export. */
  variantSnippets?: VariantSnippetForFill[];
  /** Per-document snippet selection (group slug → snippet _id). */
  snippetSelections?: Record<string, string>;
  /**
   * Resolves any generated HTML-letter attachments on this doc into renderable
   * LetterParts. PDF-form attachments (with a filledPdfStorageId) are already
   * handled by fetchAttachmentBlobs via the storage URL — this callback exists
   * solely so HTML letters, which have no filledPdfStorageId, can be rendered
   * client-side into the export.
   */
  buildHtmlLetterAttachmentParts?: () => Array<{
    name: string;
    parts: LetterParts;
  }>;
}) {
  const filename = sanitizeFilename(doc.name);

  // Bundling attachments rasterises the letter (html2canvas), which can take a
  // few seconds for a multi-page document — surface a spinner + loading toast
  // so the download doesn't feel frozen (MOR-623).
  const [isDownloading, setIsDownloading] = useState(false);

  // For PDF form templates with a filled PDF, offer direct download.
  //
  // We fetch the PDF as a blob first so the `download` attribute actually fires.
  // Convex storage URLs are cross-origin and don't set
  // `Content-Disposition: attachment`, so a plain `<a download>` click would be
  // ignored by Chrome and the browser would navigate the current tab to the PDF
  // (MOR-242). Falling back to `window.open` if the fetch fails preserves the
  // original behaviour without keeping the user stuck on the PDF view.
  // A "direct PDF" document is served as a ready-made PDF rather than rendered
  // from HTML. Two cases qualify:
  //   1. PDF form templates with a filled AcroForm (`doc.filledPdfUrl`).
  //   2. Flat form PDFs that have no extracted body HTML and no fillable
  //      fields — the only faithful output is the original uploaded file
  //      (`dbTemplate.sourceDocumentUrl`).
  //
  // Forms (`isThirdPartyForm`) skip the Anthropic HTML extraction that letters
  // get, so a flat statutory form like ASIC Form 205 never has `templateHtml`.
  // Without case 2, the PDF/Word export rendered an empty "Preview not
  // available for this document type." placeholder instead of the form itself.
  // The on-screen preview already falls back to the source PDF; this brings
  // download + print into line. `bodyHtml` is empty exactly when there is no
  // `templateHtml` and no editor-saved `editedHtml`, which is the cheap check
  // used here (mirrors getDocumentParts' body resolution).
  // MOR-2342 — a form whose fill failed has no current document. Refuse every
  // download path here, BEFORE the direct-PDF fallback would otherwise hand
  // out the blank source PDF (or a PDF from an earlier successful fill).
  const fillBlock = fillBlockReason(doc);
  if (fillBlock) {
    const explain = () => toast.error(fillBlock);
    return asText ? (
      <button
        type="button"
        onClick={explain}
        className="flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-700 transition hover:bg-red-50"
        title={fillBlock}
      >
        <Download className="size-3" />
        Download unavailable
      </button>
    ) : (
      <button
        type="button"
        onClick={explain}
        className="rounded p-1.5 text-red-500 transition hover:bg-red-50"
        title={fillBlock}
      >
        <Download className="size-3.5" />
      </button>
    );
  }

  const directPdfUrl: string | null = resolveDirectPdfUrl(doc, dbTemplate);

  if (directPdfUrl) {
    const handleDownloadDirectPdf = async () => {
      // MOR-817: fetching + consolidating the PDF can take a few seconds —
      // show the same spinner as the letter path so the click visibly lands.
      setIsDownloading(true);
      try {
        // Fetch the base PDF, the uploaded attachments, and the rendered
        // HTML annexures concurrently — they're independent (MOR-971).
        const htmlLetterParts = buildHtmlLetterAttachmentParts?.() ?? [];
        const [baseBlob, pdfAttachments, renderedHtmlLetters] =
          await Promise.all([
            fetch(directPdfUrl).then((res) => {
              if (!res.ok) throw new Error(`HTTP ${res.status}`);
              return res.blob();
            }),
            fetchAttachmentBlobs(doc),
            Promise.all(
              htmlLetterParts.map(async ({ name, parts }) => ({
                fileName: `${sanitizeFilename(name)}.pdf`,
                file: await letterPartsToPdfBlob(parts, name),
                fileType: "application/pdf" as const,
              })),
            ),
          ]);
        const attachments = [...pdfAttachments, ...renderedHtmlLetters];

        if (attachments.length === 0) {
          downloadBlob(baseBlob, `${filename}.pdf`);
          toast.success("PDF downloaded");
          return;
        }

        const { consolidated, remaining } = await consolidateAttachmentsIntoPdf(
          baseBlob,
          attachments,
        );

        if (remaining.length > 0) {
          const packageBlob = await buildAttachmentDownloadPackage({
            generatedFileName: `${filename}.pdf`,
            generatedFile: consolidated,
            attachments: remaining,
          });
          downloadBlob(packageBlob, `${filename}-with-attachments.zip`);
          toast.success("PDF package downloaded with attachments");
          return;
        }

        downloadBlob(consolidated, `${filename}.pdf`);
        toast.success("PDF downloaded with attachments");
      } catch (err) {
        console.error("Failed to download PDF, falling back to new tab", err);
        window.open(directPdfUrl, "_blank", "noopener");
      } finally {
        setIsDownloading(false);
      }
    };

    return asText ? (
      <button
        type="button"
        disabled={isDownloading}
        onClick={handleDownloadDirectPdf}
        className="flex items-center gap-1 rounded px-2 py-1 text-xs text-primary/50 hover:bg-secondary disabled:opacity-60"
      >
        {isDownloading ? (
          <Loader2 className="size-3 animate-spin" />
        ) : (
          <Download className="size-3" />
        )}
        {isDownloading ? "Preparing…" : "Download PDF"}
      </button>
    ) : (
      <button
        type="button"
        disabled={isDownloading}
        onClick={handleDownloadDirectPdf}
        className="rounded p-1.5 text-primary/40 transition hover:bg-secondary hover:text-primary/60 disabled:opacity-60"
        title={isDownloading ? "Preparing download…" : "Download PDF"}
      >
        {isDownloading ? (
          <Loader2 className="size-3.5 animate-spin" />
        ) : (
          <Download className="size-3.5" />
        )}
      </button>
    );
  }

  const handleDownloadWord = async () => {
    if (doc.pdfFillError) {
      toast.error(doc.pdfFillError);
      return;
    }
    const parts = getDocumentParts(
      doc,
      brandingProfile,
      dbTemplate,
      signatureData,
      variants,
      variantSelections,
      variantSnippets,
      snippetSelections,
      gallagherValues,
    );
    setIsDownloading(true);
    try {
      const htmlLetterParts = buildHtmlLetterAttachmentParts?.() ?? [];
      const [pdfAttachments, renderedHtmlLetters] = await Promise.all([
        fetchAttachmentBlobs(doc),
        Promise.all(
          htmlLetterParts.map(async ({ name, parts: attParts }) => ({
            fileName: `${sanitizeFilename(name)}.docx`,
            file: await buildWordBlob(attParts),
          })),
        ),
      ]);
      const attachments = [...pdfAttachments, ...renderedHtmlLetters];
      if (attachments.length === 0) {
        await downloadAsWord(parts, `${filename}.doc`);
        toast.success("Word document downloaded");
        return;
      }

      const generatedFile = await buildWordBlob(parts);
      const packageBlob = await buildAttachmentDownloadPackage({
        generatedFileName: `${filename}.docx`,
        generatedFile,
        attachments,
      });
      downloadBlob(packageBlob, `${filename}-with-attachments.zip`);
      toast.success("Word package downloaded with attachments");
    } catch (err) {
      // MOR-623: a failure gathering/rendering attachments must not silently
      // abort the download. Fall back to the letter on its own.
      console.error("Failed to bundle attachments with the Word document", err);
      toast.error(
        "Could not bundle attachments. Downloading the letter on its own.",
      );
      await downloadAsWord(parts, `${filename}.doc`);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (doc.pdfFillError) {
      toast.error(doc.pdfFillError);
      return;
    }
    const parts = getDocumentParts(
      doc,
      brandingProfile,
      dbTemplate,
      signatureData,
      variants,
      variantSelections,
      variantSnippets,
      snippetSelections,
      gallagherValues,
    );

    // Fall back to the browser's native print dialog for just the letter.
    // Used both for the no-attachment fast path and as the recovery path when
    // bundling attachments fails (MOR-623: previously an uncaught throw here
    // left the user with nothing downloaded and no feedback).
    const printLetterOnly = () => {
      if (parts.bodyHtml) {
        downloadAsPdf(parts, doc.name);
      } else {
        const html = getDocumentHtml(
          doc,
          brandingProfile,
          dbTemplate,
          signatureData,
          variants,
          variantSelections,
          variantSnippets,
          snippetSelections,
        );
        downloadAsPdf(html, doc.name);
      }
    };

    setIsDownloading(true);
    try {
      // HTML-letter attachments aren't covered by `fetchAttachmentBlobs` because
      // they have no `filledPdfStorageId` to fetch. Render them client-side into
      // PDFs so they can be merged into the consolidated output alongside any
      // PDF-form siblings and uploads. Both channels run concurrently (MOR-971).
      const htmlLetterParts = buildHtmlLetterAttachmentParts?.() ?? [];
      const [pdfAttachments, renderedHtmlLetters] = await Promise.all([
        fetchAttachmentBlobs(doc),
        Promise.all(
          htmlLetterParts.map(async ({ name, parts: attParts }) => ({
            fileName: `${sanitizeFilename(name)}.pdf`,
            file: await letterPartsToPdfBlob(attParts, name),
            fileType: "application/pdf" as const,
          })),
        ),
      ]);
      const attachments = [...pdfAttachments, ...renderedHtmlLetters];

      // No attachments: keep the existing window.print() path. It's faster
      // (no html2canvas pass) and produces selectable-text PDFs via the
      // browser's native "Save as PDF" — which is the correct experience
      // when there's nothing to bundle.
      if (attachments.length === 0) {
        printLetterOnly();
        return;
      }

      const baseBlob = await letterPartsToPdfBlob(parts, doc.name);
      const { consolidated, remaining } = await consolidateAttachmentsIntoPdf(
        baseBlob,
        attachments,
      );

      if (remaining.length > 0) {
        const packageBlob = await buildAttachmentDownloadPackage({
          generatedFileName: `${filename}.pdf`,
          generatedFile: consolidated,
          attachments: remaining,
        });
        downloadBlob(packageBlob, `${filename}-with-attachments.zip`);
        toast.success("PDF package downloaded with attachments");
        return;
      }

      downloadBlob(consolidated, `${filename}.pdf`);
      toast.success("PDF downloaded with attachments");
    } catch (err) {
      console.error("Failed to render letter PDF with attachments", err);
      toast.error(
        "Could not bundle attachments with the PDF. Opening the print dialog instead.",
      );
      printLetterOnly();
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {asText ? (
          <button
            type="button"
            disabled={isDownloading}
            className="flex items-center gap-1 rounded px-2 py-1 text-xs text-primary/50 hover:bg-secondary disabled:opacity-60"
          >
            {isDownloading ? (
              <Loader2 className="size-3 animate-spin" />
            ) : (
              <Download className="size-3" />
            )}
            {isDownloading ? "Preparing…" : "Download"}
            {!isDownloading && <ChevronDown className="size-3" />}
          </button>
        ) : (
          <button
            type="button"
            disabled={isDownloading}
            className="rounded p-1.5 text-primary/40 transition hover:bg-secondary hover:text-primary/60 disabled:opacity-60"
            title={isDownloading ? "Preparing download…" : "Download"}
          >
            {isDownloading ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Download className="size-3.5" />
            )}
          </button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DropdownMenuItem
          disabled={isDownloading}
          onSelect={() => {
            void handleDownloadWord();
          }}
        >
          <FileDown className="mr-2 size-3.5" />
          Download as Word
        </DropdownMenuItem>
        <DropdownMenuItem disabled={isDownloading} onSelect={handleDownloadPdf}>
          <FileText className="mr-2 size-3.5" />
          Download as PDF
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Format a stored date value as "16 April 2026", returning it untouched when
 * it isn't a date. Shares {@link formatDateLong} with the date-format modifiers
 * so the default long form and the modifier variants accept the same inputs —
 * including the AU day-first values the generate wizard stores.
 */
const formatDate = formatDateLong;

/**
 * Override the stored signature value with the current user's live signature
 * so that image URLs never go stale.
 */
function enrichWithSignature(
  fieldValues: Record<string, string>,
  user?: {
    signatureText?: string;
    signatureImageUrl?: string;
    signatureScale?: number;
  } | null,
): Record<string, string> {
  if (!user) return fieldValues;
  const enriched = { ...fieldValues };
  if (user.signatureImageUrl) {
    enriched.signature = buildSignatureImageToken(
      user.signatureImageUrl,
      user.signatureScale,
    );
  } else if (user.signatureText) {
    enriched.signature = user.signatureText;
  }
  return enriched;
}

/** Canonical fields that contain dates and should be formatted */
const DATE_FIELDS = new Set([
  "letter_date",
  "appointment_date",
  "court_order_date",
  "signature_date",
  "submission_date",
  "date_commenced_trading",
  "date_ceased_trading",
  "date_person_took_control",
  "date_received_report",
  "records_period_from",
  "records_period_to",
  "report_return_date",
]);

/**
 * Resolve a field value — format dates, handle signatures.
 */
/** Escape user-provided values to prevent XSS when injected into HTML templates. */
function escapeHtml(text: string): string {
  const map: Record<string, string> = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;",
  };
  return text.replace(/[&<>"']/g, (c) => map[c]);
}

function renderSignatureHtml(value: string | undefined): string {
  // MOR-623: when no signature is placed, render nothing. The previous blank
  // grey rule (`border-bottom:1px solid #999`) showed up on letters that don't
  // require a signature (e.g. annexure attachments) and read as a stray line.
  if (!value) return "";
  if (value.startsWith("__sig_image__:")) {
    const { url, scale } = parseSignatureImageToken(value);
    return `<img src="${escapeHtml(url)}" alt="Signature" style="max-height:${Math.round(60 * scale)}px;max-width:${Math.round(240 * scale)}px;object-fit:contain;margin:4px 0;" />`;
  }
  return `<span style="font-family:'Caveat',cursive;font-size:22px;line-height:1.4;">${escapeHtml(value)}</span>`;
}

/**
 * Render a checkbox field value as a glyph: ☑ when ticked, ☐ otherwise.
 * Letter checkbox chips can opt into "unchecked" mode so the same field
 * can drive two chips (a "Yes" chip ticking on checked, a "No" chip
 * ticking on unchecked). Default mode is "checked".
 */
function renderCheckboxGlyph(
  value: string | undefined,
  mode?: string | null,
): string {
  const v = (value ?? "").trim().toLowerCase();
  const fieldChecked = v === "true" || v === "yes" || v === "1";
  const ticked = mode === "unchecked" ? !fieldChecked : fieldChecked;
  return ticked ? "☑" : "☐";
}

/**
 * Collect the set of field keys that are checkboxes for a template, so
 * letter rendering can show ☑/☐ instead of literal text. Reads every
 * type source: visual field positions, legacy AcroForm mappings, and
 * extracted letter placeholders (which `updateTemplateHtml` populates
 * with the fieldType of any inserted custom field).
 */
function collectCheckboxKeys(
  dbTemplate: TemplateDoc | null | undefined,
): Set<string> {
  const keys = new Set<string>();
  for (const fp of dbTemplate?.fieldPositions ?? []) {
    if (fp?.fieldType === "checkbox" && fp.canonicalKey) {
      keys.add(fp.canonicalKey);
    }
  }
  for (const m of dbTemplate?.pdfFieldMappings ?? []) {
    if (m?.fieldType === "checkbox" && m.canonicalKey) {
      keys.add(m.canonicalKey);
    }
  }
  for (const f of dbTemplate?.extractedFields ?? []) {
    if (f?.fieldType === "checkbox" && f.key) keys.add(f.key);
  }
  return keys;
}

/**
 * The template's date-typed field keys, on top of the canonical
 * {@link DATE_FIELDS}. Org custom date fields store ISO values just like
 * canonical ones, so without this they'd render as a bare "2021-08-06"
 * instead of "6 August 2021".
 */
function collectDateKeys(
  dbTemplate: TemplateDoc | null | undefined,
): Set<string> {
  const keys = new Set(DATE_FIELDS);
  for (const fp of dbTemplate?.fieldPositions ?? []) {
    if (fp?.fieldType === "date" && fp.canonicalKey) keys.add(fp.canonicalKey);
  }
  for (const m of dbTemplate?.pdfFieldMappings ?? []) {
    if (m?.fieldType === "date" && m.canonicalKey) keys.add(m.canonicalKey);
  }
  for (const f of dbTemplate?.extractedFields ?? []) {
    if (f?.fieldType === "date" && f.key) keys.add(f.key);
  }
  return keys;
}

function resolveValue(
  key: string,
  values: Record<string, string>,
  checkboxKeys?: Set<string>,
  checkboxMode?: string | null,
  modsRaw?: string | null,
  dateKeys?: Set<string>,
): string | null {
  if (key === "signature") {
    return renderSignatureHtml(values["signature"]);
  }
  // Checkbox fields always resolve to a glyph — never "empty" — so an
  // unticked box still renders ☐ rather than a placeholder chip.
  if (checkboxKeys?.has(key)) {
    return renderCheckboxGlyph(values[key], checkboxMode);
  }
  const raw = values[key];
  if (raw == null) return null;
  // Modifiers (parens/brackets/quotes/plural/possessive/case) apply to the
  // plain-text value before HTML escaping. applyFieldModifiers short-circuits
  // on empty so an empty field never renders a bare "()".
  const mods = parseModifiers(modsRaw);
  // Date fields default to the long form (16 April 2026) unless the chip
  // carries an explicit date-format modifier, which reshapes the raw ISO value
  // itself inside applyFieldModifiers.
  const hasDateFormat = mods.some(
    (m) => MODIFIER_CATALOG[m].group === "dateformat",
  );
  const isDate = (dateKeys ?? DATE_FIELDS).has(key);
  const plain = isDate && !hasDateFormat ? formatDate(raw) : raw;
  const escaped = escapeHtml(applyFieldModifiers(plain, mods));
  // `stack` layout modifier: render the value one entry per line. Newlines
  // survive escapeHtml; applyStackLayout converts them to `<br>` (and falls
  // back to the company-name connector phrases for legacy space-joined values).
  if (mods.includes("stack")) return applyStackLayout(escaped, key);
  // Name-list fields (trading / former-trading / trust name lines) are
  // `\n`-joined; rendered inline those newlines collapse to spaces and the
  // names run together. Join them into a grammatical list instead (MOR-777).
  if (NAME_LIST_FIELDS.has(key)) return formatInlineNameList(escaped);
  return escaped;
}

/**
 * Replace {{field_key}} placeholders in HTML with actual values.
 * Strips the highlight span wrapper that extraction adds around placeholders.
 * Replaces signature dashed-border divs with blank signing space.
 * Formats ISO dates as human-readable.
 *
 * When `editing` is true, empty fields render as labelled placeholder chips and
 * the field matching `focusedField` gets a highlight ring so the user can see
 * exactly where each value appears in the document.
 */
function replaceFieldPlaceholders(
  html: string,
  values: Record<string, string>,
  options?: {
    editing?: boolean;
    focusedField?: string | null;
    classifySource?: (key: string) => FieldSource;
    checkboxKeys?: Set<string>;
    /**
     * Date-typed field keys for this template, so custom date fields get the
     * same long-form rendering as canonical ones. Defaults to
     * {@link DATE_FIELDS}.
     */
    dateKeys?: Set<string>;
    /**
     * This template's own field labels (key → label). Take precedence over
     * the canonical defaults so an empty placeholder chip reads the same
     * here as it does in the library preview, the editor and the Edit
     * Fields inputs — see `preferTemplateFieldLabel`.
     */
    fieldLabels?: Record<string, string>;
    /**
     * Resolved template collections for this document (repeat blocks /
     * inline joins / plural tokens / count conditions). Expanded BEFORE
     * scalar substitution so the stamped fragments' remaining scalar
     * chips still resolve. Omitted → collection markup degrades to
     * empty output (old precedents generated before collections).
     */
    collectionData?: Record<string, Array<Record<string, string>>>;
  },
): string {
  const {
    editing = false,
    focusedField = null,
    classifySource,
    checkboxKeys,
    dateKeys,
    fieldLabels,
    collectionData,
  } = options ?? {};

  const resolvedHtml =
    collectionData && hasCollectionMarkup(html)
      ? expandCollectionMarkup(html, collectionData)
      : html;

  const sourceAttr = (key: string) =>
    classifySource ? ` data-field-source="${classifySource(key)}"` : "";

  const renderField = (matchStr: string, key: string, modsGroup?: unknown) => {
    if (key === "signature") {
      return renderSignatureHtml(values["signature"]);
    }
    // Per-chip checkbox mode — set when the chip was inserted with a
    // specific Checked/Unchecked mode (Yes/No question support).
    const modeMatch = /data-checkbox-mode="(checked|unchecked)"/i.exec(
      matchStr,
    );
    const chipMode = modeMatch?.[1] ?? null;
    // Modifiers come from the chip's `data-field-mods` attribute (chip-span
    // form) or from the `|mod` capture group (raw / highlight-wrapped forms).
    // The chip-span regex has a single capture group, so String.replace passes
    // the numeric match offset as the 3rd arg — only treat modsGroup as mods
    // when it's actually the string capture from the {{key|mods}} forms.
    const modsAttr = /data-field-mods="([^"]+)"/i.exec(matchStr);
    const modsRaw =
      modsAttr?.[1] ?? (typeof modsGroup === "string" ? modsGroup : null);
    const resolved = resolveValue(
      key,
      values,
      checkboxKeys,
      chipMode,
      modsRaw,
      dateKeys,
    );
    const isFocused = editing && focusedField === key;
    const isEmpty = isResolvedFieldEmpty(resolved);

    if (editing && isEmpty) {
      const label =
        fieldLabels?.[key] ??
        CANONICAL_FIELDS_MAP[key]?.label ??
        key.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase());
      const cls = isFocused
        ? "field-placeholder field-placeholder--focused"
        : "field-placeholder";
      // Reflect modifiers on the empty placeholder so the field-fill view
      // matches the editor and preview (wrap chars + muted badges).
      const { open, close, badges } = modifierDecoration(
        parseModifiers(modsRaw),
      );
      const badgeHtml = badges
        .map(
          (b) =>
            `<span style="opacity:0.6;font-size:0.75em;margin-left:3px">${escapeHtml(b)}</span>`,
        )
        .join("");
      return `<span class="${cls}" data-field-key="${escapeHtml(key)}"${sourceAttr(key)}>${escapeHtml(`${open}${label}${close}`)}${badgeHtml}</span>`;
    }

    // Rendered output (not the field-fill view): leave a marker where the
    // value was empty so `collapseEmptyFieldLines` can lift the following
    // line up. Authored address blocks reserve a line for "Address Line 2";
    // without this, a contact who has none leaves a blank row and the
    // suburb/state/postcode line sits one line too low.
    if (isEmpty) return EMPTY_FIELD_MARKER;

    if (isFocused && resolved) {
      return `<span class="field-highlight" data-field-key="${escapeHtml(key)}"${sourceAttr(key)}>${resolved}</span>`;
    }

    return resolved ?? "";
  };

  const result = resolvedHtml
    .replace(
      /<div[^>]*style="[^"]*border:\s*2px dashed[^"]*"[^>]*>\{\{signature\}\}<\/div>/gi,
      renderSignatureHtml(values["signature"]),
    )
    // TipTap editor field-placeholder spans (data-field-key="key")
    .replace(
      /<span[^>]*data-field-key="(\w+)"[^>]*>[^<]*<\/span>/gi,
      renderField,
    )
    // AI extraction highlight-wrapped placeholders
    .replace(
      /<span[^>]*style="[^"]*background:\s*#FEF3C7[^"]*"[^>]*>\{\{(\w+)((?:\|[a-z0-9_]+)*)\}\}<\/span>/gi,
      renderField,
    )
    // The same highlight span after a DOM round-trip (variant resolution,
    // sanitisers) can lose its background but keep the amber text colour —
    // without this the merged VALUE renders orange in the PDF/Word output.
    .replace(
      /<span[^>]*style="[^"]*color:\s*#92400E[^"]*"[^>]*>\{\{(\w+)((?:\|[a-z0-9_]+)*)\}\}<\/span>/gi,
      renderField,
    )
    .replace(/\{\{(\w+)((?:\|[a-z0-9_]+)*)\}\}/g, renderField);
  // Lift a company status suffix ("(In Liquidation)" etc.) onto the stacked
  // company name's first line, and unwrap the data-company-stack marker so it
  // never reaches preview or Word/PDF export (MOR-634).
  // Then drop the lines whose only content was a field that came back empty
  // (and strip the markers from lines that kept other content).
  // Finally snap manual "(iii)&nbsp;&nbsp;…" list markers in hanging-indent
  // paragraphs into fixed-width boxes so justification can't stretch them out
  // of alignment (MOR-977) — covers templates saved before the editor-side
  // normalisation baked the boxes into the stored HTML.
  return normalizeManualListMarkers(
    collapseEmptyFieldLines(reflowCompanyStatus(result)),
  );
}

/**
 * Convert every field placeholder (raw `{{key|mods}}`, extraction
 * highlight-wrapped, or chip-span form) into a TipTap field-placeholder chip
 * whose label is the field's CURRENT resolved value. This seeds the day-one
 * content editor (MOR-704).
 *
 * The content editor used to bake resolved values in as plain text, so the
 * saved `editedHtml` lost all field identity — later Edit Fields changes
 * updated `fieldValues` but could never pull through into the document. By
 * seeding the editor with atomic value-labelled chips instead, TipTap
 * round-trips each field back to its `{{key|mods}}` token on save
 * (`extractHtmlFromEditor`), and the render/export paths re-substitute fresh
 * values on every render.
 *
 * Signature and checkbox placeholders keep the legacy baked form: a signature
 * resolves to image/styled HTML that a chip label can't carry, and checkbox
 * chips drop their `data-checkbox-mode` in the editor round-trip, so baking
 * the ☑/☐ glyph preserves today's behaviour for them.
 */
function bakeFieldPlaceholdersToChips(
  html: string,
  values: Record<string, string>,
  options?: {
    checkboxKeys?: Set<string>;
    /** This template's own field labels — see `replaceFieldPlaceholders`. */
    fieldLabels?: Record<string, string>;
    /**
     * Resolved template collections for this document. Expanded BEFORE
     * chip conversion so the stamped fragments' remaining scalar tokens
     * become chips — same ordering as replaceFieldPlaceholders.
     */
    collectionData?: Record<string, Array<Record<string, string>>>;
  },
): string {
  const { checkboxKeys, fieldLabels, collectionData } = options ?? {};

  const resolvedHtml =
    collectionData && hasCollectionMarkup(html)
      ? expandCollectionMarkup(html, collectionData)
      : html;

  const renderChip = (matchStr: string, key: string, modsGroup?: unknown) => {
    if (key === "signature") {
      return renderSignatureHtml(values["signature"]);
    }
    const modeMatch = /data-checkbox-mode="(checked|unchecked)"/i.exec(
      matchStr,
    );
    const chipMode = modeMatch?.[1] ?? null;
    if (checkboxKeys?.has(key) || chipMode) {
      return renderCheckboxGlyph(values[key], chipMode);
    }
    // Modifiers come from the chip's `data-field-mods` attribute (chip-span
    // form) or from the `|mod` capture group (raw / highlight-wrapped forms).
    // See replaceFieldPlaceholders for why modsGroup must be type-checked.
    const modsAttr = /data-field-mods="([^"]+)"/i.exec(matchStr);
    const modsRaw =
      modsAttr?.[1] ?? (typeof modsGroup === "string" ? modsGroup : null);
    const mods = serializeModifiers(parseModifiers(modsRaw));
    const raw = values[key];
    // The chip label is the plain (unmodified) value — the chip NodeView
    // renders wrap chars and modifier badges itself, and the real modifier
    // transform is applied by resolveValue at render time. Empty fields fall
    // back to the field's human label so the placeholder stays visible and
    // editable instead of silently vanishing from the document.
    const display =
      raw != null && raw !== ""
        ? DATE_FIELDS.has(key)
          ? formatDate(raw)
          : raw
        : (fieldLabels?.[key] ??
          CANONICAL_FIELDS_MAP[key]?.label ??
          key
            .replace(/_/g, " ")
            .replace(/\b\w/g, (c: string) => c.toUpperCase()));
    const modsSuffix = mods ? ` data-field-mods="${escapeHtml(mods)}"` : "";
    return `<span data-type="field-placeholder" class="field-placeholder-chip" data-field-key="${escapeHtml(key)}" data-field-label="${escapeHtml(display)}"${modsSuffix}>${escapeHtml(display)}</span>`;
  };

  const result = resolvedHtml
    .replace(
      /<div[^>]*style="[^"]*border:\s*2px dashed[^"]*"[^>]*>\{\{signature\}\}<\/div>/gi,
      renderSignatureHtml(values["signature"]),
    )
    // TipTap editor field-placeholder spans (data-field-key="key")
    .replace(
      /<span[^>]*data-field-key="(\w+)"[^>]*>[^<]*<\/span>/gi,
      renderChip,
    )
    // AI extraction highlight-wrapped placeholders
    .replace(
      /<span[^>]*style="[^"]*background:\s*#FEF3C7[^"]*"[^>]*>\{\{(\w+)((?:\|[a-z0-9_]+)*)\}\}<\/span>/gi,
      renderChip,
    );
  return result.replace(/\{\{(\w+)((?:\|[a-z0-9_]+)*)\}\}/g, renderChip);
}
